import { useMemo, useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react'
import { LAYOUT, COLOR, undoMoveDirection } from '../../config/uiTokens'
import { BATTLEFIELD_CAPACITIES } from '../../rules/gameConfig'
import { SUB_PER_PLOT, SUB_ROWS, battlefieldColumn, SUBPLOT_COUNT } from '../../rules/subplotGrid'
import { canAttack, ensureSlots, movedFromOf } from '../../rules/gameLogic'
import { subCenter, hexPoints, hexCorners, pixelToSub, columnSeamPoints, pointsAttr, HEX_COLS, halfEdgeY, bandHeight } from '../../utils/hexGeometry'
import useMediaQuery from '../../hooks/useMediaQuery'
import RadialMenu from './RadialMenu'
import {
    R, H, BOARD_W, BOARD_H, SIDES, LAND, HAIRLINE, ringPoints, boardPoint, plotTileBox, DRONE_RADIUS,
    CARET_DRAG_PX, GESTURE_SLOP, SWIPE_ARM_PX, corridorAt, LANE_STROKE_ARMED, LANE_STROKE_W,
} from './hex/geometry'
import HexDefs from './hex/HexDefs'
import { territoryMarks, strokeMarks, baseBarMarks, stampMarks, blastCells } from './hex/marks'
import { buildLanes, laneBox } from './hex/lanes'
import { buildTokens, CURSOR_SCALE } from './hex/tokens'
import { useTokenFlip, RISE_MS } from './hex/useTokenFlip'
import { buildPeekIntel } from './hex/peek'
import PeekSonar from './hex/PeekSonar'
import { useReveal } from './hex/useReveal'
import { HexBattleOverlays, HexFireRing, HexBlastClouds, HexDroneFlights } from './hex/battle'
import {
    buyPlacementCells, baseProgressOf, pendingBaseGhosts, zoneMarks, buyMarks, basePlacementMarks, pendingBaseMarks,
    basePlaceableRows, nearestBaseRow, baseRowAt, baseCenterAtRow, useResupplyDrops, HexResupplyDrops, useBaseBuildFx, aimBlinkMarks, ghostBarMarks, craterPreviewTint,
    pairMarks, launchBaseBuildFlight, padCells, dropZoneTethers, airdropTrails,
} from './hex/assets'
import './BattleFx.css'
import './HexBoard.css'
import GestureHand from './hex/GestureHand'

// ─── HexBoard ─────────────────────────────────────────────────────────────────
//
// The war board on a flat-top hex lattice — the Grid's 315 subplots drawn as
// hexes (utils/hexGeometry.js lays them out, utils/hexGrid.js gives the rules
// the matching adjacency). THE board: the square board (BoardGrid) it
// replaced is gone; BattleFx.css keeps the DOM pieces both shared.
//
// The two halves are exact mirrors across the centre: your Grid on your
// screen is the same shape as the enemy Grid on the opponent's. Between them
// runs a slim band of background — no yellow line, no corridor numbers.
//
// Layers, bottom to top (each in its own module under ./hex/):
//   land + grain            geometry.js / HexDefs.js
//   territory marks         marks.js   — Port, scans, own-structure wash, craters
//   corridor seams
//   zones                   assets.js  — rocket coverage, pending recon scans
//   structure strokes       marks.js   — rings, glows, Bases + their drones, rockets,
//                                        pending planes, red X
//   construction bars       marks.js   — maturation bars above the strokes
//   drone ground shadows    tokens.js  — beneath the airspace: shadows fall on land
//   caret shadows           lanes.js   — beneath the pills, cast north into the band
//   airspace lanes          lanes.js   — capacity capsules, attack-red when armed, a
//                                        yellow-and-black dash on the cap where a move left
//   intel stamps            marks.js   — above the lanes so a pill edge never crosses one
//   peek bearings           peek.js, PeekSonar.js — each live bearing's hexagon, sonar and
//                                        crossings, and each located structure's letter
//   aim marks               — each declared aim's ring, wash and corner rays; the
//                             hovered hex's at half strength
//   drop-zone tethers       assets.js  — each pending zone's arc from the Base that
//                                        earns it to its landing slot (a hovered plus
//                                        previews its own); a flying crate's trail
//   aim prompt              assets.js  — the attacking corridor's opp half blinking
//                                        red until its aim is picked (purple for a
//                                        plane awaiting its aim)
//   placement marks         assets.js  — buy tap-to-place, Build Base preview, pending
//                                        Base ghosts (their bars last, on top)
//   attack carets           lanes.js   — the pill's point + slide-up chevrons
//   drone tokens (DOM)      tokens.js  — ArmyBlock tiles (a fresh deploy stands on
//                                        the ground, small and sunk, until its submit
//                                        lifts it off), deploy / drop-zone pluses;
//                                        useTokenFlip.js slides them between renders
//                                        and plays the lift-off
//   reveal layers (DOM)     battle.js  — corridor dimming + the missile duel, the fire
//                                        ring, blast clouds, recon planes (useReveal.js
//                                        turns the hook's Grid swaps into sweep state);
//                           assets.js  — resupply crates launched from their Bases,
//                                        lobbing over onto their slots
//   radial menu (DOM)       RadialMenu — anchored on the board's own geometry
//
// The whole board is one picture: the SVG's viewBox is BOARD_W × BOARD_H in
// board units at the 375px reference width, and the board scales uniformly
// to FIT its container — no scrolling. The DOM token layer positions in
// percentages, so it scales with it, and pointer hit-testing converts
// client → board units first.
//
// INPUT — one pointer model for the whole board (touch and mouse alike):
//   tap        on a self drone → its radial menu (REMOVE a new drone, UNDO
//              MOVE a moved one, MOVE left/right on any veteran — the
//              mouse's alternative to the drag — BUILD BASE with two
//              veterans standing, the tap's alternative to the drop onto a
//              mate below: the tapped drone and its nearest fellow veteran
//              are the pair; UNDO ROCKET / UNDO DRONE for a pending
//              buy the tile sits over); on a self hex with a pending rocket
//              / plane → its UNDO confirm; on the X on a brood built
//              this round → UNDO BASE; on an attacking corridor's opp half → aim the strike
//              (onTargetPlot) — unless a pending plane in that corridor still
//              needs its aim, then the tap aims the plane (onDroneTarget);
//              on the white plus → deploy (the drone lands ON THE GROUND,
//              small and yellow, and lifts off as a veteran at SUBMIT); on
//              the yellow plus / a pending marker → place / take back a
//              drop zone — a pending zone wears the tether from the Base
//              that earns it (nearest unassigned mature Base), the arc its
//              crate will fly at delivery.
//   swipe up   (TOUCH only) anywhere on the self half of a corridor that may
//              attack → ARM it (onAction 'attack'); swipe down on an armed
//              one → disarm ('undoAttack'). The lane's caret is the
//              affordance and follows the finger; dragging the caret itself
//              works too. A mouse never swipes: on desktop attack is the
//              caret click below.
//   pick up    any veteran (or a moved-in drone) and carry it like a chess
//              piece — it follows the finger freely; the one under the
//              finger is the one that leaves (veterans are interchangeable,
//              the rest pack forward). Over a legal
//              landing (the neighbouring corridor for a MOVE, the corridor it
//              came from for an UNDO) that lane wears a subtle amber edge;
//              RELEASING there snaps it in and commits ('move' with the
//              direction / 'undoMove'), and the lane blinks once in amber.
//              Carried over ANOTHER VETERAN OF ITS OWN CORRIDOR (a mate),
//              that mate lifts and wears the amber pair ring: the two are
//              a brood's funding — two veterans that started the round
//              here; a moved-in drone neither pairs nor is a mate. RELEASING there opens the Base placement
//              step with the two as the pair (onBasePlacingStart) — the
//              cue that a corridor can build is its two veterans, and the
//              build is those two put together. A corridor whose move is
//              spent still lifts its veterans for that drop when two
//              stand. Released anywhere else it snaps back where it was.
//              A veteran that can neither move nor pair can't be picked
//              up: dragged sideways, it gives a small refusal shake.
//   click      the caret (desktop) → the ATTACK / UNDO ATTACK radial.
//   hover      (mouse) an opp hex in a corridor picking its aim → the hex
//              wears the aim mark (red strike / purple plane) at half
//              strength before the click.
//   hold       the header eye (oppPeek) → every corridor pill, drone and
//              shadow, both halves, vanishes at once (.hexb--peek), and the
//              bearings on BOTH Grids ring (the enemy's clues on yours too);
//              on release they fade back in a beat.
// A pointer is a tap until it travels GESTURE_SLOP px; a drag that started
// on a movable drone is a pick-up for the rest of the touch (it never arms),
// and any other drag on the self half is the attack swipe.
//
// PLACEMENT MODES own the board while they last (no swipes, pick-ups or
// pluses): a rocket / plane bought from the dock (buyPlacing) lights every
// eligible structure cell — tap one to station it (onBuyPlace), anywhere
// else to cancel; BUILD BASE (basePlacing, with the funding pair's uids)
// pulses the corridor's self half and turns the pair into the CURSOR: the
// brood under construction on a free row with the pair's tiles, shrunk,
// hovering over its two end cells (they fly down from their slots as the
// step opens, starting at the free row nearest them). The bar spans the
// corridor, so the row is the whole choice, and the cursor only ever
// stands on a free one: SLIDE anywhere on the board and it follows the
// finger's travel (not its position, so the finger never covers it),
// skipping taken rows; RELEASE commits it ('buildFort' with the centre and
// the pair) as the tiles drop onto the pad (launchBaseBuildFlight). A TAP
// anywhere on the self half takes the row level with it, in any column:
// a free row commits, a taken one moves the cursor to the nearest free row
// (a mouse previews by hovering, so its click commits the cursor). The
// small X on the brood's top-left corner cancels, and the pair glides back
// to its slots; a tap above the self half does nothing.
// UNDO BASE gives the pair back: their tiles rise from the pad to their
// slots (useTokenFlip's booked arrivals).
//
// THE REVEAL plays here too: useGameState steps the clash props corridor by
// corridor and swaps the Grids as strikes land; useReveal turns that into the
// fresh-crater sweep (cells ignite as the fire ring passes, structure kills
// fly to the kill bar, a fallen construction flashes and blasts as one), the
// tiles and Bases recoil and fade on the duel schedule, and the SFX;
// battle.js draws the corridor overlays, the missile duel, the ring, the
// blast clouds and the recon planes on hex geometry; after the clashes each
// drop zone's crate shoots out of its Base, crests, and floats down onto
// the slot it delivered to. Before the first clash the
// OPPONENT'S ROUND REPLAYS on their half (useGameState.playOppReplay steps
// oppBoard through buildOppReplaySteps' boards): useTokenFlip turns each
// step's board diff into motion — a cross-corridor move hops between the
// lanes, a fresh deploy glides in from their rear edge, the settle slides
// any drift into place — and useBaseBuildFx flies a new Base's funding
// drones down onto its construction, and the settle beat lifts their
// grounded deploys off as veterans. The same FLIP effect animates your own
// live moves (a menu MOVE hops; a dragged drone snaps the last stretch from
// where the finger let go), every in-corridor re-pack, and your own
// deploys' lift-off at SUBMIT.
//
// Roads are gone — the interround Build Phase and the Port were retired
// with the flip (phase 6), so there is nothing of theirs to draw.

const EMPTY = []
// Where a brood's X sits (board units): just up and left of its left end
// cell, half over a drone there like a badge — the placement cursor's
// cancel, and a brood built this round's UNDO BASE.
const broodXAt = (centerS) => {
    const pads = padCells(centerS)
    if (!pads) return null
    const { x, y } = subCenter('self', pads[0], R)
    return { x: x - 0.9 * R, y: y - 0.9 * H }
}
// The prompt bar fills the band exactly — top on the opp half's front edge,
// bottom on the self half's — so it covers neither half.
const PROMPT_BOX = {
    top: `${(halfEdgeY('opp', R) / BOARD_H) * 100}%`,
    height: `${(bandHeight(R) / BOARD_H) * 100}%`,
}
// The tutorial's corridor sweep: each corridor's pulse starts this long after
// the one to its left (HexBoard.css .hexb__corridor-sweep runs the cycle).
const CORRIDOR_SWEEP_STEP_MS = 220

// A clash's new stamps come in with their sweep (HexBoard.css
// .hexb__stamp-enter, from its sweep delay); after this they're plain ones.
export const STAMP_FLASH_MS = 8000

// Letting go of the eye (see peekOut): the peek fades out over PEEK_FADE_MS;
// PEEK_RETURN_DELAY_MS in (halfway), the drones and pills fade back over
// PEEK_RETURN_MS. Handed to HexBoard.css as custom properties.
export const PEEK_FADE_MS = 1000
export const PEEK_RETURN_DELAY_MS = 500
export const PEEK_RETURN_MS = 1500

// A structure's letter while the eye is held (PEEK_LOCATED_FONT), seated on
// its baseline half a cap height below centre so the capital sits dead
// centre (dominantBaseline="central" sits Oswald's caps low).
const PEEK_LOCATED_FONT = 13
const OSWALD_CAP = 0.74         // Oswald's cap height, as a share of its font size (measured)

// The aim mark's rays: how far past the hex's corners they reach, fading to
// nothing — one side of the hex (R, the circumradius), no further.
const AIM_RAY_LEN = R

// ── Token menus ─────────────────────────────────────────────────────────────
// Which radial a tapped self drone opens (BoardGrid's buildTokenMenu). Null =
// inert tile. hasReserve = two veterans that started the round here can
// fund a brood (a moved-in drone never can — no moving then brooding with
// the mover); the build option rides on the normal menu and is the only
// action left to a moved-out corridor's veterans.
function tokenMenu(bf, token, { isFrontMissile }) {
    const base = {
        bfId: bf.id, currentId: bf.id, movedFromId: token?.kind === 'moved' ? movedFromOf(bf, token) : bf.movedFrom,
        hasReserve: (bf.armyQty || 0) >= 2,
    }
    // The whole corridor attacks as one, so EVERY committed drone in an
    // attacking corridor pops the UNDO ATTACK confirm.
    if (bf.isAttacking) return { ...base, variant: 'attacking' }
    switch (token.kind) {
        case 'green': {
            // Resupply-delivered new drones are column-bound: once only
            // delivered ones remain in the pool, there is nothing to remove.
            const removable = (bf.newArmies || 0) - (bf.deliveredNewArmies || 0) > 0
            return removable ? { ...base, variant: 'deployArmy' } : null
        }
        case 'moved':
            // A moved-in drone can't be a brood's funding, so its menu never offers one.
            return (isFrontMissile && (bf.armyQty || 0) === 0) ? { ...base, hasReserve: false, variant: 'moved' } : { ...base, hasReserve: false, variant: 'reserveUndo' }
        case 'vet':
            // Any veteran: the one tapped is the one that moves.
            if (bf.movedOut) return base.hasReserve ? { ...base, variant: 'buildOnly' } : null
            return { ...base, variant: 'normal' }   // MOVE left / right (+ BUILD BASE)
        default:
            return null
    }
}

// A veteran may move while its corridor's move is unspent and it isn't
// attacking (BoardGrid's moveReady; gameLogic.moveUnit re-checks).
const moveEligible = (bf) => (bf.armyQty || 0) > 0 && !bf.movedOut && !bf.isAttacking

// ── The funding pair ─────────────────────────────────────────────────────────
// Two veterans that started the round in a corridor that isn't attacking
// can fund a brood — the same rule as the menu's hasReserve. A moved-in
// drone is never one of the pair, and never lifts as a mate.
const isVetToken = (t) => !!t && t.kind === 'vet'
const canPair = (bf) => !!bf && !bf.isAttacking && (bf.armyQty || 0) >= 2
// The pair a tapped veteran makes: itself and its nearest fellow veteran by
// slot, the one behind it when two are as near. Null without a mate.
function pairFor(bf, uid) {
    const slots = ensureSlots(bf).slots
    const i = slots.findIndex(t => t?.uid === uid)
    if (i === -1 || !isVetToken(slots[i])) return null
    let best = null
    slots.forEach((t, j) => {
        if (j === i || !isVetToken(t)) return
        const d = Math.abs(j - i)
        if (!best || d < best.d || (d === best.d && j > i)) best = { d, uid: t.uid }
    })
    return best ? [uid, best.uid] : null
}

// The refusal: a veteran that can't move (its corridor's move is spent)
// shakes its head when dragged sideways — 2px each way, a quarter second,
// and back where it was. Nothing else happens.
const REFUSE_PX = 2
const REFUSE_MS = 240
function refuseShake(el) {
    if (!el || typeof el.animate !== 'function') return
    el.animate([
        { transform: 'translateX(0)' },
        { transform: `translateX(${-REFUSE_PX}px)` },
        { transform: `translateX(${REFUSE_PX}px)` },
        { transform: `translateX(${-REFUSE_PX / 2}px)` },
        { transform: 'translateX(0)' },
    ], { duration: REFUSE_MS, easing: 'ease-in-out' })
}

// A mouse is a desktop pointer: no swipe-to-attack (the caret click is the
// desktop path), only clicks and pick-ups. Touch and pen swipe.
const isMouse = (e) => e.pointerType === 'mouse'

// Keep the pointer's events coming to the element that started the gesture
// even once the finger leaves it. Throws for a pointer the browser isn't
// tracking (synthetic events, some stylus edge cases) — then we just don't
// capture, and the gesture still works while the pointer stays over the board.
function capture(e) {
    try { e.currentTarget.setPointerCapture?.(e.pointerId) } catch (_) { /* not capturable */ }
}

// A brood's small X (.hexb__base-cancel): a real button the board's
// gestures leave alone (data-base-cancel).
function BroodX({ at, label, undo = null, onClick }) {
    return (
        <button type="button" className="hexb__base-cancel" data-base-cancel="true" data-brood-undo={undo ?? undefined} aria-label={label}
            style={{ left: `${at.x / BOARD_W * 100}%`, top: `${at.y / BOARD_H * 100}%` }}
            onClick={(e) => { e.stopPropagation(); onClick() }}>
            <svg viewBox="0 0 10 10" aria-hidden="true">
                <path d="M2.5 2.5 L7.5 7.5 M7.5 2.5 L2.5 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        </button>
    )
}

// ── Crosshairs ───────────────────────────────────────────────────────────────
// The strike's aim: a reticle traced on the single-margin crater's outline
// (the aimed cell and its six neighbours — the 7-hex blast a hit lands),
// arms reaching past it, and the aimed-at cell ringed in its middle (the
// cell that will wear the strike's stamp). The outer group carries the
// position (CSS transform, so a drag glides hex to hex); the body carries
// the state's motion (HexBoard.css): idle circles a tight loop, a drag (or
// a hovering mouse) swells a touch, a lock snaps in.
// Unlocked (circling, dragged, hovered) the reticle wears the aim blink's
// red — the attack caret's (COLOR.BTN_ATTACK); locked, a hotter red.
const XHAIR_COLOR = { unlocked: COLOR.BTN_ATTACK, locked: '#ff3030' }
// The crater is dark in every state — a scope's tinted glass, fine
// diagonal hatching over a vignette that deepens toward a dark rim — so it
// never reads as an airspace pill's smooth pale haze. The tone rides on
// the arms and the aimed cell.
const XHAIR_RIM = '#000000'
// Each crater cell's tint (craterPreviewTint): plain land the dark shade;
// over a Base (or one under construction) red; over a structure you know
// of, a brighter red — what the strike stands to hit.
const CRATER_TINT = {
    plain: { stripe: XHAIR_RIM, stripeOpacity: 0.15, fill: null },
    // Light stripes on a hot red: they stand off a Base's own muted red
    // (COLOR.ARMY_OPP #c04040) instead of melting into it.
    base:  { stripe: '#ffd6d6', stripeOpacity: 0.6, fill: '#ff3030', fillOpacity: 0.4 },
    known: { stripe: '#ff9090', stripeOpacity: 0.7, fill: '#ff2a2a', fillOpacity: 0.88 },
}
const hexAt0 = (r) => hexCorners(0, 0, r).map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
// The 7-hex flower's outer edge, centred on 0,0, as one closed polygon:
// every edge of the seven cells with no cell of the seven beyond it,
// chained end to end (18 edges).
// The seven cells' centres (the aimed cell first) and their hexes.
const CRATER_CENTRES = [[0, 0], [0, -H], [0, H], [1.5 * R, -H / 2], [1.5 * R, H / 2], [-1.5 * R, -H / 2], [-1.5 * R, H / 2]]
const CRATER_CELLS = CRATER_CENTRES.map(([x, y]) => hexCorners(x, y, R).map(([a, b]) => `${a.toFixed(2)},${b.toFixed(2)}`).join(' '))
const CRATER_OUTLINE = (() => {
    const centres = CRATER_CENTRES
    const isCentre = (x, y) => centres.some(([cx, cy]) => Math.hypot(cx - x, cy - y) < 0.5)
    const edges = []
    for (const [cx, cy] of centres) {
        const k = hexCorners(cx, cy, R)
        for (let i = 0; i < 6; i++) {
            const a = k[i], b = k[(i + 1) % 6]
            if (!isCentre(a[0] + b[0] - cx, a[1] + b[1] - cy)) edges.push([a, b])
        }
    }
    const near = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]) < 0.01
    const pts = [edges[0][0], edges[0][1]]
    const left = edges.slice(1)
    while (left.length) {
        const end = pts[pts.length - 1]
        const i = left.findIndex(([a, b]) => near(a, end) || near(b, end))
        if (i === -1) break
        const [a, b] = left.splice(i, 1)[0]
        pts.push(near(a, end) ? b : a)
    }
    return pts.slice(0, -1).map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
})()
// How far the flower reaches: up / down (1.5 hex heights), sideways (2.5 R).
const CRATER_REACH_Y = 1.5 * H
const CRATER_REACH_X = 2.5 * R
// The reticle's shared defs (once per board; every crosshairs and halo
// draws in its own translated space, so userSpaceOnUse resolves locally):
// the crater clip for the inner glow, the soft drop shadow, the halo's
// glow, and the white vignette wash — clear in the middle, deepening
// toward the rim.
function CrosshairDefs() {
    return (
        <defs>
            <clipPath id="hexb-xh-clip"><polygon points={CRATER_OUTLINE} /></clipPath>
            <filter id="hexb-xh-shadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="1.2" stdDeviation="1.3" floodColor="#000000" floodOpacity="0.55" />
            </filter>
            <filter id="hexb-xh-glow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="2.2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <radialGradient id="hexb-xh-wash" gradientUnits="userSpaceOnUse" cx="0" cy="0" r={CRATER_REACH_Y}>
                <stop offset="0" stopColor={XHAIR_RIM} stopOpacity="0.02" />
                <stop offset="0.55" stopColor={XHAIR_RIM} stopOpacity="0.05" />
                <stop offset="1" stopColor={XHAIR_RIM} stopOpacity="0.15" />
            </radialGradient>
            {/* Thick, even diagonal stripes: the blast as a potential, not a scar */}
            {Object.entries(CRATER_TINT).map(([kind, t]) => (
                <pattern key={kind} id={`hexb-xh-hatch-${kind}`} patternUnits="userSpaceOnUse" width="4.5" height="4.5" patternTransform="rotate(45)">
                    <rect x="0" y="0" width="1.8" height="4.5" fill={t.stripe} fillOpacity={t.stripeOpacity} />
                </pattern>
            ))}
        </defs>
    )
}

// A tapered arm along unit direction (ux, uy): a needle from r0 (its fine
// point, near the aimed cell) to r1 (its broad end, past the rim).
function armPoints(ux, uy, r0, r1, w0, w1) {
    const px = -uy, py = ux
    const p = (r, w, sgn) => `${(ux * r + px * w / 2 * sgn).toFixed(2)},${(uy * r + py * w / 2 * sgn).toFixed(2)}`
    return [p(r0, w0, 1), p(r1, w1, 1), p(r1, w1, -1), p(r0, w0, -1)].join(' ')
}

// The crosshairs draw in two layers that ride the same position and
// motion: the CRATER preview (part 'crater') sits in the board beneath the
// airspace pills, so every pill passes over it; the RETICLE (arms and the
// aimed cell, part 'reticle') rides above everything, tiles included.
function Crosshairs({ col, s, cx, cy, state, part = 'reticle', tints = null, struck = false }) {
    const tone = state === 'locked' ? 'locked' : 'unlocked'
    const color = XHAIR_COLOR[tone]
    const reach = R * 0.55
    const centreR = R - 1
    const centre = hexAt0(centreR)
    // Corner brackets on the aimed cell: a heavy dash either side of each
    // corner, nothing mid-side (one side's length per pattern).
    const k = centreR * 0.28
    const brackets = `${k.toFixed(2)} ${(centreR - 2 * k).toFixed(2)} ${k.toFixed(2)} 0`
    const arms = [
        armPoints(0, -1, H / 2 + 2.5, CRATER_REACH_Y + reach, 0.4, 3.2),
        armPoints(0, 1, H / 2 + 2.5, CRATER_REACH_Y + reach, 0.4, 3.2),
        armPoints(-1, 0, R + 2.5, CRATER_REACH_X + reach, 0.4, 3.2),
        armPoints(1, 0, R + 2.5, CRATER_REACH_X + reach, 0.4, 3.2),
    ]
    const bodyKey = state === 'locked' ? `locked-${s}` : state
    const bodyClass = `hexb__xhair-body hexb__xhair--${state}`
    if (part === 'crater') return (
        <g className={`hexb__xhair${struck ? ' hexb__xhair--struck' : ''}`} data-crosshairs-crater={col} style={{ transform: `translate(${cx}px, ${cy}px)` }}>
            <g key={bodyKey} className={bodyClass}>
                {/* The crater, cell by cell: the dark vignette and hatch over
                    plain land, red over a Base, bright red over a known
                    structure; then a dark inner shadow along the rim and the
                    solid dark rim. */}
                {CRATER_CELLS.map((pts, i) => {
                    const kind = tints?.[i] ?? 'plain'
                    const t = CRATER_TINT[kind]
                    return (
                        <g key={i} data-crater-cell={i} data-crater-tint={kind}>
                            {t.fill
                                ? <polygon points={pts} fill={t.fill} fillOpacity={t.fillOpacity} />
                                : <polygon points={pts} fill="url(#hexb-xh-wash)" />}
                            <polygon points={pts} fill={`url(#hexb-xh-hatch-${kind})`} />
                        </g>
                    )
                })}
                <polygon points={CRATER_OUTLINE} fill="none" stroke={XHAIR_RIM} strokeWidth="6" strokeOpacity="0.09" clipPath="url(#hexb-xh-clip)" />
                <polygon data-crater-outline="true" points={CRATER_OUTLINE} fill="none" stroke={XHAIR_RIM} strokeOpacity="0.28" strokeWidth="1.4"
                    strokeLinejoin="miter" strokeMiterlimit="4" />
            </g>
        </g>
    )
    return (
        <g className={`hexb__xhair${struck ? ' hexb__xhair--struck' : ''}`} data-crosshairs={col} data-crosshairs-state={state} data-crosshairs-s={s}
            data-crosshairs-struck={struck || undefined}
            data-aim-target={state === 'locked' ? s : undefined}
            style={{ transform: `translate(${cx}px, ${cy}px)` }}>
            <g key={bodyKey} className={bodyClass}>
                <g filter="url(#hexb-xh-shadow)">
                    {/* The arms: needles, broad past the rim, fine toward the cell */}
                    {arms.map((pts, i) => <polygon key={i} points={pts} fill={color} />)}
                    {/* The aimed cell: a hairline hex, heavy corner brackets, a centre point */}
                    <polygon points={centre} fill={color} fillOpacity="0.16" stroke={color} strokeOpacity="0.55" strokeWidth="0.75" />
                    <polygon points={centre} fill="none" stroke={color} strokeWidth="2.4" strokeDasharray={brackets}
                        strokeLinejoin="miter" strokeMiterlimit="4" />
                    <circle r="1.3" fill={color} />
                </g>
            </g>
        </g>
    )
}

// The lock / unlock halo — the reticle's own crater outline growing out of
// it and fading, with a soft glow: the brood pair's ripple, quick, so the
// moment reads even under a finger. UNLOCK sends one ring in the blink's red; LOCK
// sends two red ones, the second a beat behind. The rims stay hairline
// sharp as they grow (non-scaling strokes).
function AimHalo({ cx, cy, kind, onDone }) {
    const lock = kind === 'lock'
    const color = XHAIR_COLOR[lock ? 'locked' : 'unlocked']
    return (
        <g data-aim-halo={kind} style={{ transform: `translate(${cx}px, ${cy}px)` }} filter="url(#hexb-xh-glow)">
            <polygon className="hexb__xhair-halo" points={CRATER_OUTLINE} fill={color} fillOpacity="0.14" stroke={color} strokeWidth="2.5"
                strokeLinejoin="miter" strokeMiterlimit="4" vectorEffect="non-scaling-stroke" onAnimationEnd={lock ? undefined : onDone} />
            {lock && (
                <polygon className="hexb__xhair-halo hexb__xhair-halo--late" points={CRATER_OUTLINE} fill="none" stroke={color} strokeWidth="1.5"
                    strokeLinejoin="miter" strokeMiterlimit="4" vectorEffect="non-scaling-stroke" onAnimationEnd={onDone} />
            )}
        </g>
    )
}

export default function HexBoard({
    selfHf, oppHf, selfBoard, oppBoard,
    warCapacities,
    targetSubplots, targetPlots = null, onTargetPlot,
    onAction = null, isAnimating = false,
    canDeploy = false,
    dropZones = null, onDropZone = null, placingDropZones = false,
    oppPeek = false,
    // Tutorial: the opp hex the player must aim at — a large arrow bounces
    // above it, pointing down.
    aimArrow = null,
    // Tutorial: a white glove showing a gesture (hex/GestureHand.js).
    gestureHint = null,
    // Tutorial: corridors whose white deploy plus pulses.
    pulseDeploy = null,
    // Tutorial: a large white arrow down at your Hive's top edge (bobbing in
    // step with hiveOutline's pulse), and a smaller arrow down at one of your
    // own cells ({ s, color }).
    hiveArrow = false,
    selfCellArrow = null,
    // Tutorial: your Hive's outer edge, pulsing.
    hiveOutline = false,
    // Tutorial: every corridor pill, both halves, pulses white in turn, left to right.
    // true: every corridor's pill on both halves; 'self': your half only.
    corridorSweep = false,
    // Tutorial: each corridor's whole column, both Hives, lights in turn.
    columnSweep = false,
    // Tutorial: the corridors (0–6) a step uses — their pills on your half
    // pulse white, and the other corridors' hexes there are dimmed.
    highlightCorridors = null,
    // false: highlightCorridors only dims the other corridors (the step
    // points at something else in these, e.g. their deploy plus).
    highlightPills = true,
    revealOverlayUp = false, oppReadyOverlayUp = false,
    // Grid assets (useGameState's homefront actions + BattleScreen's modes)
    onHomefrontAction = null, pendingRockets = null, pendingDrones = null, onDroneTarget = null,
    buyPlacing = null, onBuyPlace = null, onBuyCancel = null,
    basePlacing = null, onBasePlacingStart = null, onBasePlacingEnd = null,
    resupplyDeliveries = null, baseBuildFx = null,
    // The reveal (useGameState's clash props)
    activeClashId = null, clashResult = null, activeClashContext = null, activeClashWinner = null,
    activeClashSelfStrength = null, activeClashOppStrength = null,
    activeClashOppTargetIndex = null, activeClashSelfTargetIndex = null,
    activeClashAbsorbed = 0, activeClashAimS = null,
    droneFlights = null, onStructureKilled = null,
    // BattleScreen's live instruction (a placement prompt), laid over the
    // band between the halves as a see-through bar (.hexb__prompt) so it
    // costs the board no height and never covers your own half. Null when
    // nothing is being asked.
    prompt = null,
}) {
    const containerRef = useRef(null)
    const boardRef = useRef(null)
    const svgRef = useRef(null)
    const isDesktop = useMediaQuery('(min-width: 768px)')
    const hostingCaps = warCapacities ?? BATTLEFIELD_CAPACITIES

    // ── Fit ────────────────────────────────────────────────────────────────
    // The board's rendered width: its container's width, or less when the
    // container is too short for the board's aspect — then it shrinks to the
    // height and centres. Measured with a ResizeObserver so rotation and
    // browser chrome changes re-fit.
    const [fitWidth, setFitWidth] = useState(null)
    useLayoutEffect(() => {
        const el = containerRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const measure = () => {
            const cs = getComputedStyle(el)
            const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
            const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)
            const w = el.clientWidth - padX
            const h = el.clientHeight - padY
            if (w <= 0 || h <= 0) return
            setFitWidth(Math.min(w, h * (BOARD_W / BOARD_H)))
        }
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    // ── Static geometry ────────────────────────────────────────────────────
    const geo = useMemo(() => {
        const cells = { opp: [], self: [] }
        for (const side of SIDES) {
            for (let s = 0; s < SUBPLOT_COUNT; s++) {
                const { x, y } = subCenter(side, s, R)
                cells[side].push({ s, cx: x, cy: y, pts: hexPoints(x, y, R) })
            }
        }
        const corridorSeams = []
        for (let sc = SUB_PER_PLOT - 1; sc < HEX_COLS - 1; sc += SUB_PER_PLOT) {
            corridorSeams.push(pointsAttr(columnSeamPoints(sc, R)))
        }
        return { cells, corridorSeams }
    }, [])
    // Each corridor's opp cells — where its crosshairs can aim — and the
    // one nearest its middle, where idle crosshairs circle.
    const corridorOppCells = useMemo(() => Array.from({ length: LAYOUT.GRID_COLS },
        (_, col) => geo.cells.opp.filter(c => battlefieldColumn(c.s) === col)), [geo])
    const corridorMidCell = useMemo(() => corridorOppCells.map((cells) => {
        if (!cells.length) return null
        const mx = cells.reduce((a, c) => a + c.cx, 0) / cells.length
        const my = cells.reduce((a, c) => a + c.cy, 0) / cells.length
        return cells.reduce((b, c) => (Math.hypot(c.cx - mx, c.cy - my) < Math.hypot(b.cx - mx, b.cy - my) ? c : b)).s
    }), [corridorOppCells])

    // ── Corridor state ─────────────────────────────────────────────────────
    const interactive = !!onAction && !isAnimating && !revealOverlayUp
    const armedCols = useMemo(() => new Set((selfBoard ?? []).flatMap((bf, c) => (bf?.isAttacking ? [c] : []))), [selfBoard])
    const eligibleCols = useMemo(() => new Set((selfBoard ?? []).flatMap((bf, c) =>
        (interactive && bf && !bf.isAttacking && canAttack(bf).valid ? [c] : []))), [selfBoard, interactive])
    const selfSlots = useMemo(() => (selfBoard ?? []).map(bf => (bf ? ensureSlots(bf) : null)), [selfBoard])
    // Corridors whose move went out this round → the side it went. A
    // moved-in drone records the SIDE it came from (movedFrom 'left' |
    // 'right'), so the right-hand neighbour saying 'left' means it came
    // from here.
    const spentMoves = useMemo(() => {
        const m = new Map()
        ;(selfBoard ?? []).forEach((bf, c) => {
            if (!bf?.movedOut) return
            const arrivedFrom = (n, side) => !!n && ensureSlots(n).slots.some(t => t?.kind === 'moved' && movedFromOf(n, t) === side)
            if (arrivedFrom(selfBoard[c + 1], 'left')) m.set(c, 'right')
            else if (arrivedFrom(selfBoard[c - 1], 'right')) m.set(c, 'left')
        })
        return m
    }, [selfBoard])

    // ── Grid assets ────────────────────────────────────────────────────────
    const rockets = pendingRockets ?? EMPTY
    const drones = pendingDrones ?? EMPTY
    const pendingDroneCells = useMemo(() => drones.map(d => d.launchS), [drones])
    // Corridors with a pending plane: still waiting for its aim / any.
    const droneAwaitCols = useMemo(() => new Set(drones.filter(d => d.targetS == null).map(d => battlefieldColumn(d.launchS))), [drones])
    const droneCols = useMemo(() => new Set(drones.map(d => battlefieldColumn(d.launchS))), [drones])
    const buyCells = useMemo(() => new Set(buyPlacing ? buyPlacementCells(selfHf, rockets, drones) : EMPTY), [buyPlacing, selfHf, rockets, drones])
    // Self Base ghosts: this round's builds (their X → UNDO BASE) and, after
    // SUBMIT, the declared builds the Grid doesn't carry yet.
    const ghosts = useMemo(() => pendingBaseGhosts(selfBoard, selfHf), [selfBoard, selfHf])
    const baseProgress = useMemo(() => ({ self: baseProgressOf(selfBoard), opp: baseProgressOf(oppBoard) }), [selfBoard, oppBoard])
    const baseAttacking = useMemo(() => ({
        self: new Set((selfBoard ?? []).flatMap((bf, c) => (bf?.isAttacking && (bf.fortCount || 0) > 0 ? [c] : []))),
        opp:  new Set((oppBoard ?? []).flatMap((bf, c) => (bf?.isAttacking && (bf.fortCount || 0) > 0 ? [c] : []))),
    }), [selfBoard, oppBoard])
    // A placement mode owns the board.
    const modal = interactive && !!(buyPlacing || basePlacing)
    // Brood placement: the corridor's free rows and the cursor's row on them.
    // cursorSr is the row asked for (fractional mid-slide); the cursor shows
    // the free row nearest it, starting from the one nearest the pair.
    const baseRows = useMemo(() => (basePlacing ? basePlaceableRows(selfHf, selfBoard, basePlacing.col) : EMPTY),
        [basePlacing, selfHf, selfBoard])
    const [cursorAsk, setCursorAsk] = useState(null)   // { sr, dir }
    useEffect(() => { setCursorAsk(null) }, [basePlacing])
    // The corridor whose drop-zone placement plus the mouse rests on: its
    // tether previews before the click.
    const [dzHover, setDzHover] = useState(null)
    useEffect(() => { if (!placingDropZones) setDzHover(null) }, [placingDropZones])

    // ── Radial menu ────────────────────────────────────────────────────────
    // Anchored in container pixels on the board's own geometry: a tapped
    // drone's centre, a cell's centre, or the lane's cap for the attack menus.
    const [radialMenu, setRadialMenu] = useState(null)
    const closeMenu = useCallback(() => setRadialMenu(null), [])
    useEffect(() => { if (!interactive || modal) setRadialMenu(null) }, [interactive, modal])
    const toContainerPx = useCallback((bx, by) => {
        const board = boardRef.current, container = containerRef.current
        if (!board || !container) return { x: bx, y: by }
        const scale = board.clientWidth / BOARD_W
        return { x: board.offsetLeft + bx * scale, y: board.offsetTop + by * scale }
    }, [])
    // menu.gapUnits (board units): how far the anchor's own shape reaches
    // toward the buttons — they sit that far out plus their radius, instead
    // of the radial's cell-derived offset.
    const openMenu = useCallback((col, menu, bx, by) => {
        const container = containerRef.current, board = boardRef.current
        if (!container) return
        const { x, y } = toContainerPx(bx, by)
        const scale = board ? board.clientWidth / BOARD_W || 1 : 1
        const { gapUnits, ...rest } = menu
        setRadialMenu({
            col, ...rest,
            anchorX: x, anchorY: y,
            offsetPx: gapUnits != null ? gapUnits * scale + LAYOUT.RADIAL_BTN_RADIUS : null,
            containerW: container.offsetWidth, containerH: container.offsetHeight,
        })
    }, [toContainerPx])
    // UNDO BASE (a built brood's X): the pair comes back — book each funded
    // drone's arrival from the pad cell it landed on, so the tiles rise
    // into their slots.
    const arrivalsRef = useRef(new Map())
    const undoBrood = useCallback((bfId, centerS) => {
        const col = battlefieldColumn(centerS)
        const pending = (selfBoard?.[col]?.pendingBaseBuilds ?? []).find(p => p.centerS === centerS)
        const pads = padCells(centerS)
        if (pending && pads) {
            ;(pending.funded ?? []).slice(0, 2).forEach((t, i) => {
                if (t?.uid == null) return
                const { x, y } = subCenter('self', pads[i], R)
                arrivalsRef.current.set(`self-${col}-${t.uid}`, { x, y, at: Date.now() })
            })
        }
        onAction?.(bfId, 'undoBuildFort', { centerS })
    }, [selfBoard, onAction])
    const handleRadialAction = useCallback((action) => {
        if (!radialMenu) return
        const { bfId, col, subS, centerS, uid } = radialMenu
        if (action === 'moveLeft' || action === 'moveRight') {
            onAction?.(bfId, 'move', { direction: action === 'moveLeft' ? 'left' : 'right', uid: uid ?? null })
        } else if (action === 'undoMove') {
            onAction?.(bfId, 'undoMove', { uid: uid ?? null })
        } else if (action === 'undoRocket' || action === 'undoDrone') {
            onHomefrontAction?.(subS, action)
        } else if (action === 'buildFort') {
            // BUILD BASE opens the placement step (BattleScreen owns the mode
            // and its prompt) with the tapped drone and its nearest fellow
            // veteran as the funding pair; the build commits once a spot is
            // picked.
            const bf = selfBoard?.[col]
            onBasePlacingStart?.(bfId, col, (bf && uid != null ? pairFor(bf, uid) : null) ?? null)
        } else if (action === 'undoBuildFort') {
            undoBrood(bfId, centerS)
        } else {
            onAction?.(bfId, action)
        }
        closeMenu()
    }, [radialMenu, selfBoard, onAction, onHomefrontAction, onBasePlacingStart, closeMenu, undoBrood])

    // ── Caret: drag to arm / disarm, click for the desktop radial ──────────
    const dragRef = useRef(null)
    const [caretLift, setCaretLift] = useState(null)   // { col, dy } for the visual follow
    const caretHandlers = useMemo(() => ({
        onDown: (e, col) => {
            e.stopPropagation()
            capture(e)
            dragRef.current = { col, startY: e.clientY, pointerId: e.pointerId, done: false, moved: false, mouse: isMouse(e) }
            setCaretLift({ col, dy: 0 })
        },
        onMove: (e) => {
            const d = dragRef.current
            if (!d || d.done || d.mouse || e.pointerId !== d.pointerId) return
            const dy = e.clientY - d.startY
            if (Math.abs(dy) > 2) d.moved = true
            setCaretLift({ col: d.col, dy })
            const bf = selfBoard?.[d.col]
            if (!bf) return
            if (!bf.isAttacking && dy <= -CARET_DRAG_PX) { d.done = true; onAction?.(bf.id, 'attack') }
            else if (bf.isAttacking && dy >= CARET_DRAG_PX) { d.done = true; onAction?.(bf.id, 'undoAttack') }
        },
        onUp: (e) => {
            e.stopPropagation()
            const d = dragRef.current
            dragRef.current = null
            setCaretLift(null)
            // A press that never dragged is a click: the ATTACK / UNDO ATTACK
            // radial, anchored on the lane's cap (the desktop path; on touch
            // it's an extra way in).
            if (d && !d.moved && !d.done) {
                const bf = selfBoard?.[d.col]
                const cap = hostingCaps[d.col]
                if (!bf || !cap) return
                const lane = laneBox('self', d.col, cap)
                openMenu(d.col, {
                    bfId: bf.id, currentId: bf.id, movedFromId: bf.movedFrom, hasReserve: false,
                    variant: bf.isAttacking ? 'attacking' : 'corridorAttack',
                }, lane.cx, lane.y)
            }
        },
    }), [selfBoard, onAction, hostingCaps, openMenu])

    // ── Board gestures: tap / swipe / drag ─────────────────────────────────
    const gestureRef = useRef(null)
    const [moveDrag, setMoveDrag] = useState(null)   // { col, uid, dx, dy, dest, mate } — the picked-up drone following the finger
    // Where a dragged drone was let go ({ key, dx, dy } — the departed tile's
    // key and the finger's offset from its slot, in px): the arrival's slide
    // starts there instead of hopping from the old slot (useTokenFlip).
    const dropRef = useRef(null)
    // Arrivals booked for tiles about to appear (useTokenFlip): key → { x, y,
    // duration, at } in board units — the pair an UNDO BASE gives back rises
    // from the pad the ghost stood on.
    const [landFlash, setLandFlash] = useState(null) // { col, key } — the lane a move just landed in
    const flashKeyRef = useRef(0)

    // The self drone under a board point, if any: { col, p, token, isFrontMissile }.
    const tokenAt = useCallback((x, y) => {
        for (let c = 0; c < selfSlots.length; c++) {
            const bf = selfSlots[c]
            if (!bf) continue
            const missiles = bf.slots.filter(t => t && t.kind !== 'base' && t.kind !== 'baseCon')
            const frontMissileUid = missiles[0]?.uid
            for (let p = 0; p < bf.slots.length; p++) {
                const token = bf.slots[p]
                if (!token) continue
                const box = plotTileBox('self', c, p)
                if (Math.hypot(x - box.cx, y - box.cy) <= DRONE_RADIUS) {
                    return { col: c, p, token, box, isFrontMissile: token.uid === frontMissileUid }
                }
            }
        }
        return null
    }, [selfSlots])

    // A pending buy whose cell sits under a drone tile can't be tapped past
    // the tile — the tile's menu carries its UNDO instead (RadialMenu's
    // withRocket / withDrone).
    const assetUnder = useCallback((box) => {
        const under = (s) => { const { x, y } = subCenter('self', s, R); return Math.hypot(x - box.cx, y - box.cy) <= DRONE_RADIUS }
        const rocket = rockets.find(under)
        if (rocket != null) return { withRocket: 'undo', subS: rocket }
        const plane = pendingDroneCells.find(under)
        if (plane != null) return { withDrone: 'undo', subS: plane }
        return null
    }, [rockets, pendingDroneCells])

    // The cursor's row: the free row nearest the one asked for — or, before
    // any ask, the one nearest the pair's tiles (they fly down to it).
    const cursorRow = useMemo(() => {
        if (!basePlacing) return null
        if (cursorAsk) return nearestBaseRow(baseRows, cursorAsk.sr, cursorAsk.dir)
        const ys = (basePlacing.uids ?? []).map(uid => {
            const p = selfSlots[basePlacing.col]?.slots.findIndex(t => t?.uid === uid) ?? -1
            return p === -1 ? null : plotTileBox('self', basePlacing.col, p).cy
        }).filter(y => y != null)
        const box = plotTileBox('self', basePlacing.col, 0)
        const sr = ys.length ? baseRowAt(basePlacing.col, box.cx, ys.reduce((a, y) => a + y, 0) / ys.length) : 0
        return nearestBaseRow(baseRows, sr ?? 0)
    }, [basePlacing, cursorAsk, baseRows, selfSlots])
    const baseCursor = basePlacing ? baseCenterAtRow(basePlacing.col, cursorRow) : null
    const cursorRowRef = useRef(null)
    cursorRowRef.current = cursorRow
    // A mouse previews by hovering: the row level with the pointer, on the
    // self half (anywhere across it).
    const hoverBaseRow = useCallback((e) => {
        if (!basePlacing || !svgRef.current) return
        const { x, y } = boardPoint(e, svgRef.current)
        const sr = baseRowAt(basePlacing.col, x, y)
        if (sr != null) setCursorAsk(prev => (prev?.sr === sr ? prev : { sr, dir: 0 }))
    }, [basePlacing])
    // The cancel X rides the cursor's top-left corner: just up and left of
    // the left end cell, half over its drone like a badge. With no free row
    // it waits at the corridor's front.
    const cancelAt = useMemo(() => {
        if (!basePlacing) return null
        return broodXAt(baseCursor ?? baseCenterAtRow(basePlacing.col, 0))
    }, [basePlacing, baseCursor])
    const commitBase = useCallback((centerS) => {
        // The pair's tiles drop from the cursor onto the pad as the build
        // commits (the clones cover the tiles the board is about to remove).
        const uids = basePlacing.uids ?? null
        if (uids) launchBaseBuildFlight({ side: 'self', col: basePlacing.col, uids, centerS, container: containerRef.current, svg: svgRef.current })
        onAction?.(basePlacing.bfId, 'buildFort', uids ? { centerS, uids } : { centerS })
        onBasePlacingEnd?.()
    }, [basePlacing, onAction, onBasePlacingEnd])

    // Which aim a tap (or a hovering mouse) on corridor `col`'s opp half
    // picks: a pending plane still needing its aim owns the corridor
    // ('drone'; then a corridor whose planes are all aimed re-aims the last
    // one as long as it isn't attacking); an attacking corridor aims its
    // strike ('attack'); otherwise nothing.
    const aimModeAt = useCallback((col) => {
        const attackHere = !!selfBoard?.[col]?.isAttacking && !!onTargetPlot
        const droneAwaitHere = droneAwaitCols.has(col) && !!onDroneTarget
        const droneHere = droneCols.has(col) && !!onDroneTarget
        if (droneAwaitHere || (!attackHere && droneHere)) return 'drone'
        return attackHere ? 'attack' : null
    }, [selfBoard, onTargetPlot, onDroneTarget, droneAwaitCols, droneCols])

    // ── Crosshairs: drag to aim the strike ─────────────────────────────────
    // Pressing anywhere in an attacking corridor's opp half UNLOCKS its
    // crosshairs: they jump under the finger (or mouse) and follow the drag,
    // their centre hex snapping to the corridor cell nearest the pointer —
    // a drag that strays out of the corridor stays aimed inside it. RELEASE
    // locks the aim there (onTargetPlot). Both moments send out the red hex
    // halo (aimPulse), so the lock reads past the finger covering it.
    const [aimDrag, setAimDrag] = useState(null)     // { col, s, hover? }
    // Where unlocked crosshairs were left (col → s): a hovering mouse that
    // leaves the corridor (or a cancelled press) parks them there, unlocked and
    // still, instead of sending them back to circling; the mouse coming back
    // into the corridor picks them up again. A lock clears its corridor's.
    const [aimPark, setAimPark] = useState({})
    const parkAim = useCallback((col, s) => setAimPark(p => (p[col] === s ? p : { ...p, [col]: s })), [])
    useEffect(() => {
        // A corridor that stops attacking, or locks, forgets its spot.
        setAimPark(p => {
            const keep = Object.entries(p).filter(([c]) => selfBoard?.[c]?.isAttacking && (targetSubplots?.[c] ?? null) === null)
            return keep.length === Object.keys(p).length ? p : Object.fromEntries(keep)
        })
    }, [selfBoard, targetSubplots])
    const [aimPulse, setAimPulse] = useState(null)   // { s, kind: 'unlock' | 'lock', key }
    const pulseKeyRef = useRef(0)
    const emitAimPulse = useCallback((s, kind) => setAimPulse({ s, kind, key: ++pulseKeyRef.current }), [])
    const aimCellAt = useCallback((col, x, y) => {
        let best = null
        for (const c of corridorOppCells[col] ?? EMPTY) {
            const d = Math.hypot(c.cx - x, c.cy - y)
            if (!best || d < best.d) best = { d, s: c.s }
        }
        return best?.s ?? null
    }, [corridorOppCells])
    useEffect(() => { if (isAnimating || revealOverlayUp || modal) setAimDrag(null) }, [isAnimating, revealOverlayUp, modal])
    // Crosshairs held for a corridor that no longer awaits a strike aim (its
    // attack undone) are let go.
    useEffect(() => { if (aimDrag && aimModeAt(aimDrag.col) !== 'attack') setAimDrag(null) }, [aimDrag, aimModeAt])
    // Desktop: a mouse hovering an attacking corridor that is still awaiting
    // its aim PICKS UP the crosshairs (hover: true). Held, they follow the
    // cursor wherever it goes — out of the corridor they track its height on
    // their own corridor's cells — until a click anywhere on the board
    // places them. A locked corridor keeps its lock until pressed.
    const hoverCrosshairAt = useCallback((e) => {
        if (!isMouse(e) || modal || isAnimating || revealOverlayUp || !svgRef.current) return null
        const { x, y } = boardPoint(e, svgRef.current)
        if (y >= BOARD_H / 2) return null
        const hit = pixelToSub(x, y, R)
        const col = hit?.side === 'opp' ? battlefieldColumn(hit.s) : corridorAt(x)
        if (aimModeAt(col) !== 'attack' || (targetSubplots?.[col] ?? null) != null) return null
        const s = aimCellAt(col, x, y)
        return s == null ? null : { col, s, hover: true }
    }, [modal, isAnimating, revealOverlayUp, aimModeAt, aimCellAt, targetSubplots])

    // Desktop aim hover: the opp hex under the mouse, in a corridor whose
    // plane picks its aim, wears the plane's purple stroke before the click
    // — the square board's per-subplot :hover. (The strike's aim is the
    // crosshairs, pressed and dragged.)
    const [aimHover, setAimHover] = useState(null)   // { s, kind }
    const hoverAimAt = useCallback((e) => {
        if (!interactive || modal || !isMouse(e) || !svgRef.current) return null
        const { x, y } = boardPoint(e, svgRef.current)
        const hit = pixelToSub(x, y, R)
        if (!hit || hit.side !== 'opp') return null
        const kind = aimModeAt(battlefieldColumn(hit.s))
        return kind === 'drone' ? { s: hit.s, kind } : null
    }, [interactive, modal, aimModeAt])
    useEffect(() => { if (!interactive || modal) setAimHover(null) }, [interactive, modal])

    const onBoardDown = useCallback((e) => {
        if (e.target.closest?.('[data-plus]') || e.target.closest?.('[data-caret]') || e.target.closest?.('[data-base-cancel]')) return
        const svg = svgRef.current
        if (!svg) return
        const { x, y } = boardPoint(e, svg)
        const hit = pixelToSub(x, y, R)
        const side = y < BOARD_H / 2 ? 'opp' : 'self'
        const col = Math.max(0, Math.min(LAYOUT.GRID_COLS - 1, Math.floor(x / (4.5 * R))))
        // A click while the mouse holds crosshairs places them, wherever it
        // lands (they're already unlocked: no halo on the press).
        if (aimDrag?.hover && isMouse(e) && !modal && !isAnimating && !revealOverlayUp) {
            gestureRef.current = { pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, aim: { col: aimDrag.col, s: aimDrag.s }, done: false, moved: false }
            setAimDrag({ col: aimDrag.col, s: aimDrag.s })
            capture(e)
            return
        }
        // A press in an attacking corridor's opp half grabs its crosshairs:
        // unlocked, under the pointer, halo out.
        if (side === 'opp' && !modal && !isAnimating && !revealOverlayUp) {
            const aimCol = hit ? battlefieldColumn(hit.s) : col
            if (aimModeAt(aimCol) === 'attack') {
                const s = aimCellAt(aimCol, x, y)
                if (s != null) {
                    gestureRef.current = { pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, aim: { col: aimCol, s }, done: false, moved: false }
                    setAimDrag({ col: aimCol, s })
                    emitAimPulse(s, 'unlock')
                    capture(e)
                    return
                }
            }
        }
        const token = side === 'self' && !modal ? tokenAt(x, y) : null
        // A movable drone under the finger makes this a pick-up: any veteran
        // while the corridor's move is unspent, or a moved-in drone (its
        // landing is home). Either may also be dropped onto a fellow veteran
        // (mates) to fund a Base when two stand — and a veteran that can't
        // move any more still lifts for that. Anything else drags as the
        // attack swipe.
        const bf = side === 'self' ? selfBoard?.[col] : null
        const mates = !!bf && isVetToken(token?.token) && canPair(bf)
        let pick = null
        if (bf && token?.token.kind === 'vet' && moveEligible(bf)) {
            pick = { kind: 'move', landings: { [col - 1]: 'left', [col + 1]: 'right' }, mates }
        } else if (bf && token?.token.kind === 'moved' && movedFromOf(bf, token.token)) {
            const home = col + (undoMoveDirection(movedFromOf(bf, token.token)) === 'left' ? -1 : 1)
            pick = { kind: 'undo', landings: { [home]: true }, mates }
        } else if (mates) {
            pick = { kind: 'pair', landings: {}, mates }
        }
        // A veteran whose corridor has already spent its move, with no mate
        // to pair with, can't be picked up: a sideways drag on it gets a
        // small refusal shake instead.
        const refuse = !!bf && token?.token.kind === 'vet' && !pick && !!bf.movedOut && !bf.isAttacking
        gestureRef.current = {
            pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, side, col, hit, token, pick, refuse,
            scale: svg.getBoundingClientRect().width / BOARD_W || 1,
            mouse: isMouse(e), modal,
            axis: null, done: false, moved: false, move: null,
            cursorSr: cursorRowRef.current,   // the brood cursor's row as the slide starts
        }
        capture(e)
    }, [tokenAt, selfBoard, modal, isAnimating, revealOverlayUp, aimModeAt, aimCellAt, emitAimPulse, aimDrag])

    const onBoardMove = useCallback((e) => {
        const g = gestureRef.current
        if (!g) {
            // No gesture: the brood cursor and the aim hover follow a
            // hovering mouse.
            if (basePlacing && isMouse(e)) hoverBaseRow(e)
            const hover = hoverAimAt(e)
            setAimHover(prev => (prev?.s === hover?.s && prev?.kind === hover?.kind ? prev : hover))
            if (isMouse(e)) {
                if (aimDrag?.hover) {
                    // Held: follow the cursor, held to the crosshairs' corridor.
                    const { x, y } = boardPoint(e, svgRef.current)
                    const s = aimCellAt(aimDrag.col, x, y)
                    if (s != null && s !== aimDrag.s) setAimDrag({ ...aimDrag, s })
                } else {
                    const xh = hoverCrosshairAt(e)
                    if (xh) setAimDrag(xh)
                }
            }
            return
        }
        if (g.done || e.pointerId !== g.pointerId) return
        if (g.aim) {
            // The crosshairs follow the pointer, held to their corridor.
            if (!svgRef.current) return
            const { x, y } = boardPoint(e, svgRef.current)
            const s = aimCellAt(g.aim.col, x, y)
            if (s != null && s !== g.aim.s) { g.aim.s = s; setAimDrag({ col: g.aim.col, s }) }
            return
        }
        const dx = e.clientX - g.x0, dy = e.clientY - g.y0
        if (!g.axis) {
            if (Math.hypot(dx, dy) < GESTURE_SLOP) return
            g.axis = Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x'
            g.moved = true
        }
        if (g.modal) {
            // The slide: the cursor travels a row per row-pitch of the
            // finger's travel from where it stood, landing on free rows.
            if (basePlacing && g.cursorSr != null) setCursorAsk({ sr: g.cursorSr + dy / (H * g.scale), dir: Math.sign(dy) })
            return
        }
        if (!interactive || g.side !== 'self') return
        const bf = selfBoard?.[g.col]
        if (!bf) return
        if (g.refuse && g.axis === 'x') {
            // The move is spent: the tile shakes its head, very slightly, and
            // the gesture is over.
            g.done = true
            refuseShake(boardRef.current?.querySelector(`[data-token-key="self-${g.col}-${g.token.token.uid}"]`))
            return
        }
        if (g.pick) {
            // The pick-up: the tile rides under the finger; the corridor under
            // the tile's centre is the candidate landing (legal ones edge their
            // lane), or a fellow veteran of its own corridor under it is the
            // mate (it lifts, ringed); the action is decided at release.
            const bx = g.token.box.cx + dx / g.scale
            const by = g.token.box.cy + dy / g.scale
            const over = g.pick.mates ? tokenAt(bx, by) : null
            const mate = over && over.col === g.col && over.token.uid !== g.token.token.uid && isVetToken(over.token) ? over.token.uid : null
            const under = corridorAt(bx)
            const legal = mate == null && under !== g.col && g.pick.landings[under] != null && under >= 0 && under < LAYOUT.GRID_COLS
            g.move = mate != null
                ? { action: 'pair', extras: { uids: [g.token.token.uid, mate] }, dest: null }
                : legal
                    ? (g.pick.kind === 'move'
                        ? { action: 'move', extras: { direction: g.pick.landings[under], uid: g.token.token.uid }, dest: under }
                        : { action: 'undoMove', extras: { uid: g.token.token.uid }, dest: under })
                    : null
            setMoveDrag({ col: g.col, uid: g.token.token.uid, dx, dy, dest: g.move?.dest ?? null, mate })
            return
        }
        if (g.axis === 'y' && !g.mouse) {
            // The attack swipe (touch only), from anywhere in the corridor. The
            // caret rides along (a third of the way) as the visible follow.
            const canArm = !bf.isAttacking && eligibleCols.has(g.col)
            const canDisarm = bf.isAttacking
            if (!canArm && !canDisarm) return
            setCaretLift({ col: g.col, dy: dy / 3 })
            if (canArm && dy <= -SWIPE_ARM_PX) { g.done = true; setCaretLift(null); onAction?.(bf.id, 'attack') }
            else if (canDisarm && dy >= SWIPE_ARM_PX) { g.done = true; setCaretLift(null); onAction?.(bf.id, 'undoAttack') }
        }
    }, [interactive, selfBoard, eligibleCols, onAction, basePlacing, hoverBaseRow, hoverAimAt, tokenAt, aimCellAt, hoverCrosshairAt, aimDrag])

    // Held crosshairs stay held off the board too: they wait where they are
    // and follow again when the cursor comes back.
    const onBoardLeave = useCallback(() => setAimHover(null), [])

    const onBoardUp = useCallback((e) => {
        const g = gestureRef.current
        gestureRef.current = null
        setMoveDrag(null)
        setCaretLift(null)
        if (!g) return
        // The crosshairs set down: the aim locks where they stand.
        if (g.aim) {
            setAimDrag(null)
            onTargetPlot?.(g.aim.col, g.aim.s)
            emitAimPulse(g.aim.s, 'lock')
            return
        }
        // A picked-up drone released over a legal landing snaps in: the move
        // (or the undo) commits and the landing lane blinks once in amber.
        // Released anywhere else, the tile has already snapped home.
        if (g.move) {
            const bf = selfBoard?.[g.col]
            if (bf && g.move.action === 'pair') {
                // Dropped on its mate: the two are the pair; the tile settles
                // back into its slot and the placement step opens on them.
                onBasePlacingStart?.(bf.id, g.col, g.move.extras.uids)
            } else if (bf) {
                dropRef.current = { key: `self-${g.col}-${g.token.token.uid}`, dx: e.clientX - g.x0, dy: e.clientY - g.y0, at: Date.now() }
                if (g.move.extras) onAction?.(bf.id, g.move.action, g.move.extras)
                else onAction?.(bf.id, g.move.action)
                setLandFlash({ col: g.move.dest, key: ++flashKeyRef.current })
            }
            return
        }
        // A slide ends on release: the cursor's spot commits.
        if (g.moved && g.modal && basePlacing) {
            if (baseCursor != null) commitBase(baseCursor)
            return
        }
        if (g.moved && !g.modal) return
        if (g.done) return
        // A TAP.
        // Placement modes first — they own every tap while they last.
        if (g.modal && buyPlacing) {
            if (g.hit?.side === 'self' && buyCells.has(g.hit.s)) onBuyPlace?.(g.hit.s)
            else onBuyCancel?.()
            return
        }
        if (g.modal && basePlacing) {
            const svg = svgRef.current
            const pt = svg ? boardPoint(e, svg) : null
            const sr = pt ? baseRowAt(basePlacing.col, pt.x, pt.y) : null
            if (sr == null) return                                         // above the self half: nothing (the X cancels)
            if (g.mouse) { if (baseCursor != null) commitBase(baseCursor); return }
            if (baseRows.includes(sr)) commitBase(baseCenterAtRow(basePlacing.col, sr))
            else setCursorAsk({ sr, dir: 0 })                               // a taken row: the cursor goes to the nearest free one
            return
        }
        if (g.side === 'self') {
            if (!interactive) return
            const bf = selfBoard?.[g.col]
            if (!bf) return
            if (g.token) {
                const menu = tokenMenu(bf, g.token.token, g.token)
                const asset = assetUnder(g.token.box)
                const full = menu ? { ...menu, ...(asset ?? {}), uid: g.token.token.uid } : asset ? { bfId: bf.id, currentId: bf.id, movedFromId: bf.movedFrom, hasReserve: false, variant: asset.withDrone ? 'undoDrone' : 'undoRocket', subS: asset.subS } : null
                if (full) openMenu(g.col, full, g.token.box.cx, g.token.box.cy)
                return
            }
            // A bare self hex: a pending buy on it pops its UNDO (a brood
            // built this round undoes from its X instead).
            if (!g.hit) return
            const s = g.hit.s
            const { x, y } = subCenter('self', s, R)
            const common = { bfId: bf.id, currentId: bf.id, movedFromId: bf.movedFrom, hasReserve: false }
            if (rockets.includes(s)) openMenu(g.col, { ...common, variant: 'undoRocket', subS: s }, x, y)
            else if (pendingDroneCells.includes(s)) openMenu(g.col, { ...common, variant: 'undoDrone', subS: s }, x, y)
            return
        }
        // Opp half: the corridor's aim mode (aimModeAt) says whether the tap
        // aims a plane or the strike.
        if (g.hit && !revealOverlayUp && !isAnimating) {
            const col = battlefieldColumn(g.hit.s)
            const mode = aimModeAt(col)
            if (mode === 'drone') onDroneTarget(col, g.hit.s)
            else if (mode === 'attack') onTargetPlot(col, g.hit.s)
        }
    }, [interactive, isAnimating, selfBoard, onAction, onTargetPlot, onDroneTarget, revealOverlayUp, openMenu,
        buyPlacing, buyCells, onBuyPlace, onBuyCancel, basePlacing, baseRows, baseCursor, commitBase, onBasePlacingStart,
        assetUnder, rockets, pendingDroneCells, aimModeAt, emitAimPulse])

    const onBoardCancel = useCallback(() => {
        const g = gestureRef.current
        gestureRef.current = null
        setMoveDrag(null)
        setCaretLift(null)
        if (g?.aim) parkAim(g.aim.col, g.aim.s)
        setAimDrag(null)
    }, [parkAim])

    // Both Grids ring while the eye is held: your bearings on theirs, and
    // theirs on yours (what the enemy knows about you).
    // Letting go of the eye: the peek's layers fade out over PEEK_FADE_MS
    // (still drawn meanwhile — peekOut), and halfway through that fade the
    // drones, pills and shadows start fading back in over PEEK_RETURN_MS
    // (dronesBack holds the board's slow-return class until they're in;
    // HexBoard.css .hexb--peek-out).
    const [peekOut, setPeekOut] = useState(false)
    const [dronesBack, setDronesBack] = useState(false)
    const wasPeeking = useRef(false)
    useEffect(() => {
        if (oppPeek) {
            wasPeeking.current = true
            setPeekOut(false)
            setDronesBack(false)
            return undefined
        }
        if (!wasPeeking.current) return undefined
        wasPeeking.current = false
        setPeekOut(true)
        setDronesBack(true)
        const out = setTimeout(() => setPeekOut(false), PEEK_FADE_MS)
        const back = setTimeout(() => setDronesBack(false), PEEK_RETURN_DELAY_MS + PEEK_RETURN_MS)
        return () => { clearTimeout(out); clearTimeout(back) }
    }, [oppPeek])
    const peekShown = oppPeek || peekOut
    const peekLayer = `hexb__peek-layer${!oppPeek && peekOut ? ' hexb__peek-layer--out' : ''}`
    // Each Hive's intel, always: the board's stamps hide those of found
    // (located) structures with or without the eye; the peek draws the rest.
    const intel = useMemo(() => ({ opp: buildPeekIntel(oppHf), self: buildPeekIntel(selfHf, { own: true }) }), [oppHf, selfHf])
    const peekIntel = peekShown ? intel : null

    // ── The reveal ─────────────────────────────────────────────────────────
    const reveal = useReveal({
        selfHf, oppHf, selfBoard, oppBoard, isAnimating,
        activeClashId, clashResult, activeClashContext, activeClashWinner,
        activeClashSelfStrength, activeClashOppStrength, activeClashAbsorbed, activeClashAimS,
        activeClashSelfTargetIndex, activeClashOppTargetIndex, targetPlots,
        droneFlights, onStructureKilled,
    })
    // The corridors whose strike has landed this reveal: a crater swapped
    // onto their Hive from their declared aim. Their crosshairs fade off then.
    const [struckCols, setStruckCols] = useState(() => new Set())
    useEffect(() => { setStruckCols(new Set()) }, [isAnimating])
    useEffect(() => {
        const origin = reveal.fireOrigin.opp
        if (!isAnimating || origin == null) return
        const col = (targetSubplots ?? []).findIndex(s => s === origin)
        if (col >= 0) setStruckCols(prev => (prev.has(col) ? prev : new Set([...prev, col])))
    }, [isAnimating, reveal.fireOrigin.opp, reveal.fresh.opp, targetSubplots])
    const resupply = useResupplyDrops({ selfBoard, oppBoard, isAnimating, resupplyDeliveries })
    // Nests whose egg has left: in the air now (the crate IS the egg until
    // its chute opens), or flown earlier this delivery cycle — a spent nest
    // must not grow the egg back once its airdrop has gone. Reset when a
    // reveal starts: a standing brood lays a fresh egg for the next round.
    const flownEggsRef = useRef({ self: new Set(), opp: new Set() })
    useEffect(() => { if (isAnimating) flownEggsRef.current = { self: new Set(), opp: new Set() } }, [isAnimating])
    const inFlightEggs = useMemo(() => {
        const out = { self: new Set(), opp: new Set() }
        for (const d of resupply.drops) if (d.fromS != null) { out[d.side].add(d.fromS); flownEggsRef.current[d.side].add(d.fromS) }
        return out
    }, [resupply.drops])
    // A live brood's egg is hidden only while in the air (a fresh one is laid
    // as the crate lands); a spent nest's stays hidden once it has flown.
    const spentOrFlown = useCallback((side, hf) => {
        const out = new Set(inFlightEggs[side])
        for (const st of Object.values(hf?.broods ?? {})) {
            if (st.spent && flownEggsRef.current[side].has(st.footprint?.[0])) out.add(st.footprint[0])
        }
        return out
    }, [inFlightEggs])
    useBaseBuildFx({ baseBuildFx, containerRef, svgRef })
    useTokenFlip({ boardRef, dropRef, arrivalsRef })

    // Stamps the latest clash just made (fresh craters and scans): they come
    // in on their sweep's clock (.hexb__stamp-enter) and then stay, like every
    // stamp; after STAMP_FLASH_MS they're plain ones.
    const [stampFlash, setStampFlash] = useState(null)
    useEffect(() => {
        const sides = {}
        for (const side of ['self', 'opp']) sides[side] = new Set([...reveal.fresh[side], ...reveal.freshScan[side]])
        if (!sides.self.size && !sides.opp.size) return undefined
        setStampFlash(sides)
        const t = setTimeout(() => setStampFlash(null), STAMP_FLASH_MS)
        return () => clearTimeout(t)
    }, [reveal.fresh, reveal.freshScan])

    // ── Layers ─────────────────────────────────────────────────────────────
    const land = (side) => geo.cells[side].map(({ s, pts }) => (
        <polygon key={s} points={pts} fill={LAND[side].fill} stroke={HAIRLINE} strokeWidth="0.5" />
    ))

    // `peek`: the intel peek's own pass (stamps in their structure's hue,
    // found structures' stamps hidden); every other pass draws them plain.
    const marks = (side, hf, fn, { peek = false } = {}) => {
        if (!hf?.subplots) return null
        const ctx = {
            peekColors: peek ? peekIntel?.[side]?.centerColor ?? null : null,
            hiddenStamps: intel[side]?.hiddenStamps ?? null,
            fresh: reveal.fresh[side], fireOrigin: reveal.fireOrigin[side],
            freshScan: reveal.freshScan[side], scanOrigins: reveal.scanOrigins[side],
            pendingRockets: side === 'self' ? rockets : EMPTY,
            pendingDroneCells: side === 'self' ? pendingDroneCells : EMPTY,
            baseProgress: baseProgress[side], baseAttacking: baseAttacking[side],
            baseFx: reveal.baseFxFor(side), conCollapse: reveal.conCollapse[side],
            hiddenEggs: spentOrFlown(side, hf),
            entering: peek ? null : stampFlash,
        }
        return geo.cells[side].map(({ s, cx, cy, pts }) => {
            const els = fn(side, hf, s, hf.subplots[s], pts, cx, cy, ctx)
            return els.length ? <g key={s}>{els}</g> : null
        })
    }

    const hoverCol = moveDrag?.dest ?? null
    // No carets during the reveal or a placement mode — the corridors are
    // fighting, or the board is spoken for.
    const caretCols = useMemo(() => (isAnimating || modal ? new Set() : armedCols), [isAnimating, modal, armedCols])
    const laneInteractive = interactive && !modal
    // The corridors whose crosshairs are hunting (dragged, or awaiting their
    // aim): their opp pill clears away. A locked aim brings it back.
    const aimingCols = useMemo(() => {
        const out = new Set()
        if (isAnimating) return out
        if (aimDrag) out.add(aimDrag.col)
        if (interactive && onTargetPlot) (selfBoard ?? []).forEach((bf, c) => {
            if (bf?.isAttacking && (targetSubplots?.[c] ?? null) === null && aimModeAt(c) === 'attack') out.add(c)
        })
        return out
    }, [isAnimating, aimDrag, interactive, onTargetPlot, selfBoard, targetSubplots, aimModeAt])
    const { lanes, carets, caretShadows } = useMemo(
        () => buildLanes({ hostingCaps, armedCols: caretCols, eligibleCols: modal ? new Set() : eligibleCols, caretLift, interactive: laneInteractive, caretHandlers, hoverCol, flash: landFlash, spentMoves: isAnimating ? null : spentMoves, aimingCols }),
        [hostingCaps, caretCols, eligibleCols, modal, caretLift, laneInteractive, caretHandlers, hoverCol, landFlash, spentMoves, isAnimating, aimingCols],
    )

    const onDeploy = useCallback((bf) => onAction?.(bf.id, 'deploy'), [onAction])
    // The funding pair, while a Base's spot is picked: the cursor's drones,
    // each over its end cell of the brood (at[uid], board units — the pads
    // the build flight lands them on). A drag hovering a mate shows the
    // mate's ring alone (pairMarks).
    const funding = useMemo(() => {
        if (!interactive || basePlacing?.uids?.length !== 2) return null
        const pads = baseCursor != null ? padCells(baseCursor) : null
        const at = pads ? Object.fromEntries(basePlacing.uids.map((uid, i) => [uid, subCenter('self', pads[i], R)])) : null
        return { col: basePlacing.col, uids: basePlacing.uids, at }
    }, [interactive, basePlacing, baseCursor])
    const pair = useMemo(() => {
        const slotOfIn = (col) => (uid) => { const i = selfSlots[col]?.slots.findIndex(t => t?.uid === uid) ?? -1; return i === -1 ? null : i }
        if (moveDrag?.mate != null) return pairMarks({ col: moveDrag.col, uids: [moveDrag.mate], slotOf: slotOfIn(moveDrag.col), link: false })
        return EMPTY
    }, [moveDrag, selfSlots])
    // Cancelled: the cursor's drones glide from where they hovered back up
    // to their slots (a commit removes the tiles instead — they flew down).
    const lastFundingRef = useRef(null)
    useLayoutEffect(() => {
        const was = lastFundingRef.current
        lastFundingRef.current = funding
        if (!was?.at || funding) return
        const board = boardRef.current
        const scale = board ? board.clientWidth / BOARD_W || 1 : 1
        for (const uid of was.uids) {
            const el = board?.querySelector(`[data-token-key="self-${was.col}-${uid}"]`)
            const p = selfSlots[was.col]?.slots.findIndex(t => t?.uid === uid) ?? -1
            if (!el || p === -1 || typeof el.animate !== 'function') continue
            const box = plotTileBox('self', was.col, p), from = was.at[uid]
            el.animate([
                { transform: `translate(${(from.x - box.cx) * scale}px, ${(from.y - box.cy) * scale}px) scale(${CURSOR_SCALE})` },
                { transform: 'none' },
            ], { duration: RISE_MS, easing: 'ease-in-out' })
        }
    }, [funding, selfSlots])
    const pulseDeployKey = pulseDeploy ? pulseDeploy.join(',') : ''
    const pulseDeploySet = useMemo(() => (pulseDeployKey ? new Set(pulseDeployKey.split(',').map(Number)) : null), [pulseDeployKey])
    const { tokens, shadows } = useMemo(() => buildTokens({
        selfBoard, oppBoard, hostingCaps,
        canDeploy: interactive && canDeploy, onDeploy, pulseDeploy: pulseDeploySet,
        dropZones: dropZones ?? [], placingDropZones: interactive && placingDropZones, onDropZone, onDropZoneHover: setDzHover,
        moveDrag, funding,
        launched: reveal.launched, shake: reveal.shake,
        hidden: resupply.hidden, landed: resupply.landed, modal,
    }), [selfBoard, oppBoard, hostingCaps, interactive, canDeploy, onDeploy, pulseDeploySet, dropZones, placingDropZones, onDropZone, moveDrag, funding,
        reveal.launched, reveal.shake, resupply.hidden, resupply.landed, modal])

    // Drop-zone tethers: each pending zone's arc from its Base to its slot,
    // and the hovered plus's preview, while the round is open. The pending
    // zones keep their tethers through the wait for the opponent (the
    // frost dims them with the markers). A crate in flight draws its trail.
    const tethers = useMemo(() => {
        if (isAnimating || !(dropZones?.length || (interactive && placingDropZones))) return EMPTY
        return dropZoneTethers({
            selfBoard, hostingCaps, dropZones: dropZones ?? [],
            canDeploy: interactive && canDeploy, placing: interactive && placingDropZones && !modal,
            hoverCol: interactive && placingDropZones && !modal ? dzHover : null,
        })
    }, [isAnimating, dropZones, interactive, placingDropZones, selfBoard, hostingCaps, canDeploy, modal, dzHover])
    const trails = useMemo(() => airdropTrails(resupply.drops), [resupply.drops])

    const blasts = useMemo(() => [
        ...blastCells('opp',  oppHf,  reveal.fresh.opp,  reveal.fireOrigin.opp,  geo.cells.opp,  reveal.conCollapse.opp),
        ...blastCells('self', selfHf, reveal.fresh.self, reveal.fireOrigin.self, geo.cells.self, reveal.conCollapse.self),
    ], [oppHf, selfHf, reveal.fresh, reveal.fireOrigin, reveal.conCollapse, geo])

    // Zones: rocket coverage on both Grids, pending recon scans.
    const zones = useMemo(() => zoneMarks({
        selfHf, oppHf, pendingRockets: rockets, pendingDrones: drones, cells: geo.cells,
    }), [selfHf, oppHf, rockets, drones, geo])

    // "Choose your target": every armed corridor still without an aim cell
    // blinks its opp half (red), as does every corridor whose plane still
    // awaits its aim (purple) — while the board can take the tap.
    const attackAwaitCols = useMemo(() => new Set((selfBoard ?? []).flatMap((bf, c) =>
        (interactive && onTargetPlot && bf?.isAttacking && (targetSubplots?.[c] ?? null) === null ? [c] : []))),
        [selfBoard, targetSubplots, interactive, onTargetPlot])
    const aimBlink = useMemo(() => aimBlinkMarks({
        attackAwaitCols, droneAwaitCols: interactive && onDroneTarget ? droneAwaitCols : null, cells: geo.cells,
    }), [attackAwaitCols, droneAwaitCols, interactive, onDroneTarget, geo])

    // Outline paths on your half: every hex edge of the cells `inside(s)` with
    // no inside cell beyond it (a neighbour's centre is this centre mirrored
    // through the edge's middle; `same(s, n)` says whether neighbour n counts).
    const outlinePath = useCallback((inside, same) => {
        const key = (x, y) => `${Math.round(x * 10)},${Math.round(y * 10)}`
        const byCentre = new Map(geo.cells.self.map(c => [key(c.cx, c.cy), c.s]))
        let d = ''
        for (const c of geo.cells.self) {
            if (!inside(c.s)) continue
            const k = hexCorners(c.cx, c.cy, R)
            for (let i = 0; i < 6; i++) {
                const [x1, y1] = k[i], [x2, y2] = k[(i + 1) % 6]
                const n = byCentre.get(key(x1 + x2 - c.cx, y1 + y2 - c.cy))
                if (n != null && same(c.s, n)) continue
                d += `M${x1.toFixed(2)},${y1.toFixed(2)}L${x2.toFixed(2)},${y2.toFixed(2)}`
            }
        }
        return d
    }, [geo])
    // Your Hive's jagged outer edge.
    const hiveOutlinePath = useMemo(() => outlinePath(() => true, () => true), [outlinePath])
    // Each of your structures' outer edge (not the seams between its own hexes).
    const structureOutlinePath = useMemo(() => {
        if (!hiveOutline || !selfHf?.subplots) return ''
        const id = (s) => {
            const sid = selfHf.subplots[s]?.structureId
            return sid != null && selfHf.structures?.[sid] ? sid : null
        }
        return outlinePath((s) => id(s) != null, (s, n) => id(n) === id(s))
    }, [hiveOutline, selfHf, outlinePath])

    // The step's corridors: their pills on your half, and every other
    // corridor's hexes there dimmed.
    const corridorHighlight = useMemo(() => {
        if (!highlightCorridors?.length) return null
        const lit = new Set(highlightCorridors)
        const maxCap = SUB_ROWS / SUB_PER_PLOT
        return {
            pills: [...lit].map(c => ({ c, ...laneBox('self', c, Math.min(hostingCaps[c] ?? 0, maxCap)) })),
            dim: geo.cells.self.filter(c => !lit.has(battlefieldColumn(c.s))),
        }
    }, [highlightCorridors, hostingCaps, geo])

    // The corridor sweep: a white outline over each pill, both halves, each
    // corridor's pulse a beat after the one to its left (CSS delay by column).
    const corridorSweepMarks = useMemo(() => {
        if (!corridorSweep) return null
        const maxCap = SUB_ROWS / SUB_PER_PLOT
        const out = []
        hostingCaps.forEach((capRaw, c) => {
            const cap = Math.min(capRaw ?? 0, maxCap)
            if (cap <= 0) return
            for (const side of corridorSweep === 'self' ? ['self'] : ['opp', 'self']) {
                const { x, y, w, h } = laneBox(side, c, cap)
                out.push(
                    <rect key={`${side}-${c}`} className="hexb__corridor-sweep" data-corridor-sweep={`${side}-${c}`}
                        x={x} y={y} width={w} height={h} rx={w / 2}
                        fill="none" stroke="#ffffff" strokeWidth="2.5"
                        style={{ animationDelay: `${c * CORRIDOR_SWEEP_STEP_MS}ms` }} />
                )
            }
        })
        return out
    }, [corridorSweep, hostingCaps])

    // The column sweep: each corridor's full column — its three hex columns
    // through both Hives — washes white in turn, on the corridor sweep's beat.
    const columnSweepMarks = useMemo(() => {
        if (!columnSweep) return null
        return hostingCaps.map((_, c) => (
            <g key={c} className="hexb__corridor-sweep" data-column-sweep={c}
                style={{ animationDelay: `${c * CORRIDOR_SWEEP_STEP_MS}ms` }}>
                {['opp', 'self'].flatMap(side => geo.cells[side].filter(cell => battlefieldColumn(cell.s) === c).map(cell => (
                    <polygon key={`${side}-${cell.s}`} points={cell.pts} fill="#ffffff" fillOpacity="0.45" />
                )))}
            </g>
        ))
    }, [columnSweep, hostingCaps, geo])

    // The tutorial's aim arrow: tip just above the hex's top edge, shaft
    // rising three hex-heights; the inner group bounces (HexBoard.css).
    const aimArrowMark = useMemo(() => {
        if (aimArrow == null) return null
        const c = geo.cells.opp[aimArrow]
        const tipY = c.cy - R * 0.55
        const len = R * 3.4, head = R * 1.3, hw = R * 1.05, sw = R * 0.38
        const pts = [[0, 0], [-hw, -head], [-sw, -head], [-sw, -len], [sw, -len], [sw, -head], [hw, -head]]
            .map(([x, y]) => `${(c.cx + x).toFixed(2)},${(tipY + y).toFixed(2)}`).join(' ')
        return (
            <g className="hexb__aim-arrow" data-aim-arrow={aimArrow} style={{ pointerEvents: 'none' }}>
                <polygon points={c.pts} fill="#f0e040" fillOpacity="0.22" />
                <polygon className="hexb__aim-arrow-body" points={pts}
                    fill="#f0e040" stroke="#111" strokeWidth="1.5" strokeLinejoin="round" />
            </g>
        )
    }, [aimArrow, geo])

    // A downward arrow with its tip at (x, tipY): shaft `len` long, head
    // `head` deep and `hw` half-wide, shaft `sw` half-wide.
    const downArrowPoints = (x, tipY, len, head, hw, sw) =>
        [[0, 0], [-hw, -head], [-sw, -head], [-sw, -len], [sw, -len], [sw, -head], [hw, -head]]
            .map(([dx, dy]) => `${(x + dx).toFixed(2)},${(tipY + dy).toFixed(2)}`).join(' ')

    // The tutorial's arrows at your own Hive: a large white one, top centre,
    // at the Hive's top edge; a smaller one at a cell (your Outpost).
    const selfArrowMarks = useMemo(() => {
        const out = []
        if (hiveArrow) {
            const pts = downArrowPoints(BOARD_W / 2, halfEdgeY('self', R) - 3, R * 3.6, R * 1.9, R * 1.8, R * 0.6)
            out.push(
                <g key="hive" className="hexb__hive-arrow" data-hive-arrow="true">
                    <polygon className="hexb__hive-arrow-body" points={pts} fill="#ffffff" strokeLinejoin="round" />
                </g>
            )
        }
        if (selfCellArrow) {
            // Tip just above the hex's top edge (the cells are flat-topped:
            // the edge is half a hex-height above the centre).
            const c = geo.cells.self[selfCellArrow.s]
            const pts = downArrowPoints(c.cx, c.cy - H / 2 - 1.5, R * 2.2, R * 1.2, R * 1.15, R * 0.38)
            out.push(
                <g key="cell" data-self-cell-arrow={selfCellArrow.s}>
                    <polygon className="hexb__cell-arrow-body" points={pts} fill={selfCellArrow.color ?? COLOR.STRUCT_SELF} />
                </g>
            )
        }
        return out.length ? <g style={{ pointerEvents: 'none' }}>{out}</g> : null
    }, [hiveArrow, selfCellArrow, geo])

    // Placement marks: the buy highlights, the Base placement preview, the
    // Base ghosts (this round's builds; declared ones through the wait).
    const placement = useMemo(() => {
        const out = []
        if (interactive && buyPlacing) out.push(...buyMarks({ mode: buyPlacing, cells: [...buyCells], geo: geo.cells }))
        if (interactive && basePlacing) out.push(...basePlacementMarks({ col: basePlacing.col, cursor: baseCursor, rows: baseRows, geo: geo.cells }))
        out.push(...pendingBaseMarks({ ghosts, geo: geo.cells }))
        // The ghosts' bars LAST, above their own fills and rings.
        out.push(...ghostBarMarks({ ghosts }))
        return out
    }, [interactive, buyPlacing, buyCells, basePlacing, baseCursor, baseRows, ghosts, geo])

    // Labelled structures (peek.js located), only while the eye is held:
    // their filled hexes are painted solid in the letter's hue, UNDER the
    // stamp layer so a bearing on one still reads; a cratered hex is left to
    // its X. One hex carries the letter — dark on a filled hex, in the hue
    // with a dark edge on a crater.
    const peekLocatedFill = peekIntel ? SIDES.flatMap((side) => [...(peekIntel[side]?.located ?? [])]
        .filter(([, { fill }]) => fill)
        .map(([s, { letter, color }]) => (
            <polygon key={`${side}-${s}`} className="hexb__peek-located" data-peek-side={side} data-peek-letter={letter}
                data-peek-fill="true" points={geo.cells[side][s].pts} fill={color} />
        ))) : null
    const peekLocated = peekIntel ? SIDES.flatMap((side) => [...(peekIntel[side]?.located ?? [])]
        .filter(([, { label }]) => label)
        .map(([s, { letter, color, fill }]) => {
            const c = geo.cells[side][s]
            return (
                <text key={`${side}-${s}`} data-peek-label={letter} data-peek-side={side} x={c.cx} y={c.cy + PEEK_LOCATED_FONT * OSWALD_CAP / 2}
                    textAnchor="middle" fontFamily="'Oswald', sans-serif" fontSize={PEEK_LOCATED_FONT} fontWeight="700"
                    fill={fill ? '#0a0806' : color} stroke={fill ? undefined : '#0a0806'} strokeWidth={fill ? undefined : 2.5}
                    paintOrder="stroke" strokeLinejoin="round">{letter}</text>
            )
        })) : null

    // Aim marks — .bg__hf-subtarget--selected: 2px #e63030 inset + faint red
    // fill on each corridor's declared aim, with six rays off the hex's
    // corners fading outward (one radial gradient centred on the hex): the
    // blast a successful strike lands reaches past this plot. The hovered
    // hex (desktop) wears the same mark at half strength — red, or purple
    // while a plane picks its aim (.bg__hf-subtarget:hover /
    // .bg__hf-subgrid--drone).
    const aimMark = (key, s, color, { opacity = 1, attrs = {} } = {}) => {
        const c = geo.cells.opp[s]
        const reach = R + AIM_RAY_LEN
        const gradId = `hexb-aim-rays-${key}`
        return (
            <g key={key} opacity={opacity} style={{ pointerEvents: 'none' }} {...attrs}>
                <radialGradient id={gradId} gradientUnits="userSpaceOnUse" cx={c.cx} cy={c.cy} r={reach}>
                    <stop offset={R / reach} stopColor={color} stopOpacity="0.85" />
                    <stop offset="1" stopColor={color} stopOpacity="0" />
                </radialGradient>
                <g data-aim-rays="true" fill="none" stroke={`url(#${gradId})`} strokeWidth="1.5" strokeLinecap="round">
                    {hexCorners(c.cx, c.cy, R).map(([x, y], i) => (
                        <line key={i} x1={x} y1={y} x2={c.cx + (x - c.cx) * (reach / R)} y2={c.cy + (y - c.cy) * (reach / R)} />
                    ))}
                </g>
                <polygon points={c.pts} fill={color} fillOpacity="0.15" />
                <polygon points={ringPoints(c.cx, c.cy, 2)} fill="none" stroke={color} strokeWidth="2" />
            </g>
        )
    }
    // The declared aims wear the crosshairs (below); the small mark is left
    // to a plane's hover.
    const frames = []

    // The crosshairs, per attacking corridor while the round is open:
    // dragged (under the pointer), locked (on the declared aim), or idle —
    // awaiting the aim, circling a tight loop in the corridor's middle.
    // Through the clash animations every declared aim keeps its locked
    // crosshairs, just as they stood when it was set — until that corridor's
    // first strike lands on the Hive, when they fade off (struckCols).
    const crosshairs = []
    {
        for (let col = 0; col < LAYOUT.GRID_COLS; col++) {
            const declared = targetSubplots?.[col] ?? null
            let s = null, state = null
            if (isAnimating) { if (declared != null) { s = declared; state = 'locked' } }
            else if (aimDrag?.col === col) { s = aimDrag.s; state = 'drag' }
            else if (declared != null && selfBoard?.[col]?.isAttacking) { s = declared; state = 'locked' }
            else if (attackAwaitCols.has(col) && aimModeAt(col) === 'attack') {
                if (aimPark[col] != null) { s = aimPark[col]; state = 'parked' }
                else { s = corridorMidCell[col]; state = 'idle' }
            }
            if (s == null) continue
            const c = geo.cells.opp[s]
            // What each of the crater's seven cells would hit.
            const tints = CRATER_CENTRES.map(([dx, dy]) => {
                const hit = pixelToSub(c.cx + dx, c.cy + dy, R)
                return hit?.side === 'opp' ? craterPreviewTint(oppHf, hit.s) : null
            })
            crosshairs.push(<Crosshairs key={`xhair-${col}`} col={col} s={s} cx={c.cx} cy={c.cy} state={state} tints={tints}
                struck={isAnimating && struckCols.has(col)} />)
        }
    }
    // The holes the unlocked crosshairs cut in the aim blink: the crater's
    // shape (a touch larger) riding the same position and motion.
    const blinkHoles = crosshairs.filter(x => x.props.state !== 'locked').map(x => (
        <g key={x.key} className="hexb__xhair" style={{ transform: `translate(${x.props.cx}px, ${x.props.cy}px)` }}>
            <g className={`hexb__xhair-body hexb__xhair--${x.props.state}`}>
                <polygon points={CRATER_OUTLINE} fill="#000000" transform="scale(1.06)" />
            </g>
        </g>
    ))
    // The opp drones ghost down while any crosshairs are hunting.
    const aimingActive = crosshairs.some(x => x.props.state !== 'locked')
    const halo = aimPulse && !isAnimating ? (() => {
        const c = geo.cells.opp[aimPulse.s]
        return <AimHalo key={aimPulse.key} cx={c.cx} cy={c.cy} kind={aimPulse.kind}
            onDone={() => setAimPulse(p => (p?.key === aimPulse.key ? null : p))} />
    })() : null
    if (aimHover) {
        frames.push(aimMark('aim-hover', aimHover.s, aimHover.kind === 'drone' ? '#a855f7' : '#e63030',
            { opacity: 0.5, attrs: { 'data-aim-hover': aimHover.s, 'data-aim-kind': aimHover.kind } }))
    }

    return (
        <div ref={containerRef} className="bg__container hexb" data-testid="hex-container"
            style={{ aspectRatio: `${BOARD_W} / ${BOARD_H}` }}>
            <div
                ref={boardRef}
                className={`hexb__board${oppPeek ? ' hexb--peek' : dronesBack ? ' hexb--peek-out' : ''}${aimingActive ? ' hexb--aiming' : ''}`}
                style={{
                    ...(fitWidth != null ? { width: fitWidth } : null),
                    '--peek-fade': `${PEEK_FADE_MS}ms`,
                    '--peek-return': `${PEEK_RETURN_MS}ms`,
                    '--peek-return-delay': `${PEEK_RETURN_DELAY_MS}ms`,
                }}
                onPointerDown={onBoardDown}
                onPointerMove={onBoardMove}
                onPointerUp={onBoardUp}
                onPointerCancel={onBoardCancel}
                onPointerLeave={onBoardLeave}
                data-testid="hex-board-surface"
            >
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${BOARD_W} ${BOARD_H}`}
                    width="100%"
                    style={{ display: 'block', cursor: basePlacing || aimDrag ? 'crosshair' : 'pointer' }}
                    data-testid="hex-board"
                >
                    <HexDefs landClipCells={geo.cells} />

                    {/* Land */}
                    <g>{land('opp')}{land('self')}</g>
                    <rect x="0" y="0" width={BOARD_W} height={BOARD_H} filter="url(#hexb-noise)" clipPath="url(#hexb-land-clip)" style={{ pointerEvents: 'none' }} />

                    {/* Territory: Port, scans, own-structure wash, craters */}
                    <g>{marks('opp', oppHf, territoryMarks)}{marks('self', selfHf, territoryMarks)}</g>

                    {/* Corridor boundaries — the square board's column gap (#111) at half
                        its width (1.5px): the zigzag reads heavier than a straight gap.
                        Drawn BENEATH the structure strokes: the seam's stroke straddles
                        the shared edge and would otherwise cover a footprint ring's
                        outer pixel wherever a building touches a corridor edge. */}
                    <g fill="none" stroke={COLOR.BG} strokeWidth={LAYOUT.COL_GAP / 2} strokeLinejoin="round">
                        {geo.corridorSeams.map((p, i) => <polyline key={i} points={p} />)}
                    </g>

                    {/* Zones: rocket coverage, pending recon scans — beneath the strokes
                        like the square board's coverage tiles */}
                    <g style={{ pointerEvents: 'none' }}>{zones}</g>

                    {/* Structure strokes: rings, glows, Bases, rockets, planes, red X */}
                    <g>{marks('opp', oppHf, strokeMarks)}{marks('self', selfHf, strokeMarks)}</g>

                    {/* Construction maturation bars — above the strokes, so a footprint's
                        side cell never paints over the bar's end. (A ghost's bar rides at
                        the end of the placement layer, above the ghost's own ring.) */}
                    <g style={{ pointerEvents: 'none' }}>{marks('opp', oppHf, baseBarMarks)}{marks('self', selfHf, baseBarMarks)}</g>

                    {/* Tutorial: the corridors a step doesn't use, dimmed on your half */}
                    {corridorHighlight && (
                        <g data-corridor-dim="true" style={{ pointerEvents: 'none' }}>
                            {corridorHighlight.dim.map(c => <polygon key={c.s} points={c.pts} fill="rgba(0, 0, 0, 0.32)" />)}
                        </g>
                    )}

                    {/* Drone ground shadows — on the land, BENEATH the airspace lanes */}
                    <g style={{ pointerEvents: 'none' }}>{shadows}</g>

                    {/* The crosshairs' crater previews — beneath the pills, which pass
                        over them (the reticles ride the top layer). The defs live
                        here; the top layer's reticles and halos use them too. */}
                    {(crosshairs.length > 0 || halo) && (
                        <g style={{ pointerEvents: 'none' }}>
                            <CrosshairDefs />
                            {crosshairs.map(x => <Crosshairs key={`crater-${x.props.col}`} {...x.props} part="crater" />)}
                        </g>
                    )}

                    {/* Caret shadows — beneath the lanes, so a pill covers its own caret's
                        shadow and only the band takes it. */}
                    <g data-caret-layer="shadows" style={{ pointerEvents: 'none' }}>{caretShadows}</g>

                    {/* Airspace lanes — one capsule per corridor per half */}
                    <g style={{ pointerEvents: 'none' }}>{lanes}</g>

                    {/* Tutorial: the swipe slide's lane turns armed as the
                        swipe ends (HexBoard.css, in step with the glove). */}
                    {gestureHint?.kind === 'swipe' && (() => {
                        const { x, y, w, h } = laneBox('self', gestureHint.col, hostingCaps[gestureHint.col])
                        return (
                            <g className="hexb__gesture-armed" data-gesture-armed={gestureHint.col} style={{ pointerEvents: 'none' }}>
                                <rect x={x} y={y} width={w} height={h} rx={w / 2}
                                    fill="url(#hexb-lane-haze-armed)" filter="url(#hexb-lane-feather)" />
                                <rect x={x} y={y} width={w} height={h} rx={w / 2} fill="none"
                                    stroke={LANE_STROKE_ARMED} strokeWidth={LANE_STROKE_W} strokeLinejoin="round" strokeLinecap="round" />
                            </g>
                        )
                    })()}

                    {/* Intel stamps — above the lanes so a pill edge never crosses a number */}
                    {peekLocatedFill && <g className={peekLayer} style={{ pointerEvents: 'none' }}>{peekLocatedFill}</g>}
                    {/* The plain layer holds every live stamp but a found structure's;
                        it hides while the eye is held (HexBoard.css [data-stamp-layer]),
                        and the peek's coloured copies fade out with the rest of the peek. */}
                    <g data-stamp-layer="true" style={{ pointerEvents: 'none' }}>{marks('opp', oppHf, stampMarks)}{marks('self', selfHf, stampMarks)}</g>
                    {peekIntel && (
                        <g className={peekLayer} data-peek-stamps="true" style={{ pointerEvents: 'none' }}>
                            {marks('opp', oppHf, stampMarks, { peek: true })}{marks('self', selfHf, stampMarks, { peek: true })}
                        </g>
                    )}

                    {/* Tutorial: the step's corridor pills on your half, pulsing */}
                    {corridorHighlight && highlightPills && (
                        <g className="hexb__hive-outline" style={{ pointerEvents: 'none' }}>
                            {corridorHighlight.pills.map(({ c, x, y, w, h }) => (
                                <rect key={c} data-corridor-highlight={c} x={x} y={y} width={w} height={h} rx={w / 2}
                                    fill="none" stroke="#ffffff" strokeWidth="2.5" />
                            ))}
                        </g>
                    )}

                    {/* Tutorial: the corridor sweep */}
                    {corridorSweepMarks && <g style={{ pointerEvents: 'none' }}>{corridorSweepMarks}</g>}
                    {columnSweepMarks && <g style={{ pointerEvents: 'none' }}>{columnSweepMarks}</g>}

                    {/* Tutorial: your Hive's outline */}
                    {hiveOutline && (
                        <g className="hexb__hive-outline" style={{ pointerEvents: 'none' }}>
                            <path data-hive-outline="true" d={hiveOutlinePath}
                                fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
                            {/* …and your structures', in their colour, on the same pulse */}
                            <path data-structure-outline="true" d={structureOutlinePath}
                                fill="none" stroke={COLOR.STRUCT_SELF} strokeWidth="1.5" strokeLinecap="round" />
                        </g>
                    )}

                    {/* Intel peek rings */}
                    <g className={peekLayer}>
                        <PeekSonar intel={peekIntel} geo={geo} sides={SIDES} />
                        {peekLocated && <g style={{ pointerEvents: 'none' }}>{peekLocated}</g>}
                    </g>

                    {/* Drop-zone tethers and the flying crates' trails — above the
                        lanes and stamps so the dashes read whole, beneath the aim marks */}
                    <g style={{ pointerEvents: 'none' }}>{tethers}{trails}</g>

                    {/* Aim frames */}
                    <g style={{ pointerEvents: 'none' }}>{frames}</g>

                    {/* Aim prompt: the awaiting corridors' opp halves blink. Above the
                        lanes and stamps so the outline reads whole. */}
                    {/* Cut away under each unlocked crosshairs, moving with them, so
                        the aim reads over the line */}
                    {blinkHoles.length > 0 && (
                        <mask id="hexb-blink-mask" maskUnits="userSpaceOnUse" x="0" y="0" width={BOARD_W} height={BOARD_H}>
                            <rect x="0" y="0" width={BOARD_W} height={BOARD_H} fill="#ffffff" />
                            {blinkHoles}
                        </mask>
                    )}
                    <g style={{ pointerEvents: 'none' }} mask={blinkHoles.length > 0 ? 'url(#hexb-blink-mask)' : undefined}>{aimBlink}</g>

                    {/* Tutorial: the arrow at the hex to aim at */}
                    {aimArrowMark}

                    {/* Tutorial: the arrows at your Hive's edge and your Outpost */}
                    {selfArrowMarks}

                    {/* Placement: buy highlights, Base placement preview, pending ghosts */}
                    <g style={{ pointerEvents: 'none' }}>{placement}</g>

                    {/* The funding pair's rings and link — beneath the tiles (DOM) */}
                    <g style={{ pointerEvents: 'none' }}>{pair}</g>

                    {/* Attack carets — the eligible / armed self lanes' points, above
                        everything so a finger can always reach them. */}
                    <g data-caret-layer="carets">{carets}</g>
                </svg>

                {/* Drone tiles, deploy / drop-zone pluses — DOM, over the SVG. Tiles
                    are click-through (the board hit-tests them); the pluses and
                    markers are real buttons. .hexb__tokens strips the tiles' CSS
                    drop-shadow (HexBoard.css); the SVG shadows stand in. */}
                <div className={`hexb__tokens${modal ? ' hexb__tokens--modal' : ''}`} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>{tokens}</div>

                {/* Tutorial: the gesture hand, over the tiles. */}
                {gestureHint && <GestureHand hint={gestureHint} selfBoard={selfBoard} caps={hostingCaps} />}

                {/* The crosshairs and their lock / unlock halo — their own SVG over
                    the drone tiles, so a tile never covers the aim. */}
                {(crosshairs.length > 0 || halo) && (
                    <svg className="hexb__xhair-layer" viewBox={`0 0 ${BOARD_W} ${BOARD_H}`} width="100%" aria-hidden="true"
                        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
                        {crosshairs}{halo}
                    </svg>
                )}

                {/* ── The reveal ── */}
                {isAnimating && (
                    <HexBattleOverlays
                        selfBoard={selfBoard} oppBoard={oppBoard}
                        activeClashId={activeClashId} clashResult={clashResult}
                        activeClashContext={activeClashContext} activeClashWinner={activeClashWinner}
                        activeClashSelfStrength={activeClashSelfStrength} activeClashOppStrength={activeClashOppStrength}
                        activeClashOppTargetIndex={activeClashOppTargetIndex} activeClashSelfTargetIndex={activeClashSelfTargetIndex}
                        activeClashAbsorbed={activeClashAbsorbed} activeClashAimS={activeClashAimS}
                        targetPlots={targetPlots}
                        cells={geo.cells}
                    />
                )}
                {reveal.fireRing && (
                    <HexFireRing key={reveal.fireRing.id} side={reveal.fireRing.side} aimS={reveal.fireRing.aimS}
                        maxDist={reveal.fireRing.maxDist} onDone={reveal.clearFireRing} />
                )}
                <HexBlastClouds cells={blasts} />
                {droneFlights && <HexDroneFlights flights={droneFlights} />}
                <HexResupplyDrops drops={resupply.drops} onLanded={resupply.onLanded} boardRef={boardRef} />

                {revealOverlayUp && <div className="bg__self-frost" />}
                {oppReadyOverlayUp && <div className="bg__opp-frost" />}

                {/* Brood placement's cancel: the X on the cursor's top-left corner */}
                {interactive && cancelAt && (
                    <BroodX at={cancelAt} label="Cancel brood" onClick={() => onBasePlacingEnd?.()} />
                )}
                {/* This round's builds keep their X until SUBMIT: UNDO BASE */}
                {interactive && !modal && [...ghosts].filter(([, g]) => g.undoable).map(([centerS, g]) => {
                    const at = broodXAt(centerS)
                    return at && <BroodX key={centerS} at={at} label="Undo brood" undo={centerS} onClick={() => undoBrood(g.bfId, centerS)} />
                })}

                {/* The live instruction, exactly in the band between the halves */}
                <div className="hexb__prompt" data-testid="prompt-slot" style={PROMPT_BOX}>{prompt}</div>
            </div>

            {/* Radial menu — a direct child of the container so its absolute
                coordinates align with the board's container pixels. */}
            {radialMenu && (
                <RadialMenu
                    variant={radialMenu.variant}
                    col={radialMenu.col}
                    row={0}
                    anchorX={radialMenu.anchorX}
                    anchorY={radialMenu.anchorY}
                    movedFromId={radialMenu.movedFromId}
                    currentId={radialMenu.currentId}
                    hasReserve={radialMenu.hasReserve}
                    withRocket={radialMenu.withRocket}
                    withDrone={radialMenu.withDrone}
                    containerWidth={radialMenu.containerW}
                    containerHeight={radialMenu.containerH}
                    offset={radialMenu.offsetPx}
                    isDesktop={isDesktop}
                    onAction={handleRadialAction}
                    onClose={closeMenu}
                />
            )}
        </div>
    )
}
